import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import ContractFeeSettings from './ContractFeeSettings';
import ContractChangeHistory from './ContractChangeHistory';
import BudgetStylePicker from './BudgetStylePicker';
import { usesLineMarkups } from '../utils/contractFees';

const ProjectContracts = () => {
  // Currency formatting function
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '$0.00';
    const num = parseFloat(value);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const location = useLocation();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [projectCostCodes, setProjectCostCodes] = useState([]);
  const [costTypes, setCostTypes] = useState([]);
  const [accountingPeriods, setAccountingPeriods] = useState([]);
  const [projectBudgetLines, setProjectBudgetLines] = useState([]);
  const [projectBudgetLinesLoading, setProjectBudgetLinesLoading] = useState(false);
  const [unallocatedCosts, setUnallocatedCosts] = useState([]);
  const [externalChangeOrders, setExternalChangeOrders] = useState([]);
  const [allExternalChangeOrders, setAllExternalChangeOrders] = useState([]);
  const [changeOrderLines, setChangeOrderLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [copyingBudgetLines, setCopyingBudgetLines] = useState(false);
  const [budgetLinesProgress, setBudgetLinesProgress] = useState({ current: 0, total: 0 });
  
  // Allocation management state
  const [showAllocationsModal, setShowAllocationsModal] = useState(false);
  const [selectedItemForAllocations, setSelectedItemForAllocations] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [allocationsByItem, setAllocationsByItem] = useState({});
  const [newAllocation, setNewAllocation] = useState({
    cost_code_vuid: '',
    cost_type_vuid: '',
  });
  
  // Add allocation form states
  const [showAddAllocationForm, setShowAddAllocationForm] = useState(false);
  const [itemAllocations, setItemAllocations] = useState([]);
  
  // Fee level and fee settings state
  const [feeLevel, setFeeLevel] = useState(null);
  const [feeSettings, setFeeSettings] = useState([]);
  
  // Integration states
  const [showIntegrationModal, setShowIntegrationModal] = useState(false);
  const [showRetrieveModal, setShowRetrieveModal] = useState(false);
  const [selectedContractForIntegration, setSelectedContractForIntegration] = useState(null);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [sendingToIntegration, setSendingToIntegration] = useState(false);
  
  // Form states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showItemsForm, setShowItemsForm] = useState(false);
  const [showFeeSettings, setShowFeeSettings] = useState(false);
  const [showContractHistory, setShowContractHistory] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractItems, setContractItems] = useState([]);
  const [contractItemsLoading, setContractItemsLoading] = useState(false);
  const [contractItemSort, setContractItemSort] = useState({ key: 'item_number', direction: 'asc' });
  
  // Ref to track last processed contract to prevent infinite loops
  const lastProcessedContractRef = useRef(null);
  const savingNewItemRef = useRef(false);
  const savedContractItemsRef = useRef(new Map());
  // Ref for the contract form header to scroll/focus when creating new contract
  const contractFormHeaderRef = useRef(null);
  
  // Form data
  const CONTRACT_TYPES = [
    { value: 'cost_plus', label: 'Cost Plus' },
    { value: 'gmp', label: 'GMP' },
    { value: 'time_and_materials', label: 'T&M' },
    { value: 'fixed_price', label: 'Fixed Price' },
  ];

  const [formData, setFormData] = useState({
    project_vuid: '',
    contract_number: '',
    contract_date: '',
    customer_vuid: '',
    accounting_period_vuid: '',
    contract_type: CONTRACT_TYPES[0].value,
    status: 'active',
    description: '',
    total_amount: '',
    currency: 'USD',
    payment_terms: '',
    delivery_terms: '',
    warranty_terms: '',
    start_date: '',
    end_date: '',
    delivery_date: '',
    notes: ''
  });
  
  const [itemFormData, setItemFormData] = useState({
    item_number: '',
    description: '',
    quantity: 1,
    unit_of_measure: 'EA',
    unit_price: '',
    total_amount: '',
    markup_percentage: 0,
    status: 'active',
    cost_code_vuid: null,
    cost_type_vuid: null,
    specifications: '',
    delivery_location: '',
    delivery_date: '',
    warranty_info: '',
    notes: '',
    apply_fee_to_labor_schedule: false,
    contract_vuid: ''
  });
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [contractsPerPage] = useState(10);
  
  // Project settings and allocation status
  const [projectSettings, setProjectSettings] = useState({});
  const [allocationStatus, setAllocationStatus] = useState({});
  const [showAllocationAlert, setShowAllocationAlert] = useState(false);
  
  // Dropdown states
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [accountingPeriodDropdownOpen, setAccountingPeriodDropdownOpen] = useState(false);
  const [contractTypeDropdownOpen, setContractTypeDropdownOpen] = useState(false);
  
  // Search states for smart dropdowns
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  
  // Errors
  const [errors, setErrors] = useState({});
  const [itemErrors, setItemErrors] = useState({});

  // State for contract items grid
  const [newItemData, setNewItemData] = useState({
    item_number: '',
    cost_code_vuid: '',
    cost_type_vuid: '',
    description: '',
    total_amount: '',
    markup_percentage: 0,
    apply_fee_to_labor_schedule: false,
    notes: ''
  });

  // State for dropdowns
  const [costCodeDropdownOpen, setCostCodeDropdownOpen] = useState(null);
  const [costTypeDropdownOpen, setCostTypeDropdownOpen] = useState(null);
  const [newItemCostCodeDropdownOpen, setNewItemCostCodeDropdownOpen] = useState(false);
  const [newItemCostTypeDropdownOpen, setNewItemCostTypeDropdownOpen] = useState(false);

  // State for filtered dropdowns
  const [filteredCostCodes, setFilteredCostCodes] = useState([]);
  const [filteredCostTypes, setFilteredCostTypes] = useState([]);
  const [laborScheduleCostCodes, setLaborScheduleCostCodes] = useState(new Set());
  const [markupDrafts, setMarkupDrafts] = useState({});
  const [notesColumnOpen, setNotesColumnOpen] = useState(false);

  const baseURL = 'http://localhost:5001';

  useEffect(() => {
    fetchData();
  }, []);

  // Add click outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setProjectDropdownOpen(false);
        setStatusDropdownOpen(false);
        setCustomerDropdownOpen(false);
        setAccountingPeriodDropdownOpen(false);
        setContractTypeDropdownOpen(false);
        setCostCodeDropdownOpen(null);
        setCostTypeDropdownOpen(null);
        setNewItemCostCodeDropdownOpen(false);
        setNewItemCostTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    filterAndPaginateContracts();
  }, [contracts, searchTerm, projectFilter, statusFilter, currentPage]);

  // Reset cost type selection when cost code changes
  useEffect(() => {
    if (newAllocation.cost_code_vuid) {
      setNewAllocation(prev => ({ ...prev, cost_type_vuid: '' }));
    }
  }, [newAllocation.cost_code_vuid]);

  // Fetch project's budget line combinations when a contract is selected/viewed
  const showCreateFormRef = useRef(showCreateForm);
  showCreateFormRef.current = showCreateForm;

  useEffect(() => {
    const projectVuid = selectedContract?.project_vuid;
    if (projectVuid) {
      axios
        .get(`${baseURL}/api/projects/${projectVuid}/budget-lines`)
        .then((res) => setProjectBudgetLines(Array.isArray(res.data) ? res.data : []))
        .catch((err) => {
          console.warn('Failed to fetch project budget lines:', err);
          setProjectBudgetLines([]);
        });
    } else if (!showCreateFormRef.current) {
      setProjectBudgetLines([]);
    }
  }, [selectedContract]);

  // Handle query parameters for project filtering and contract viewing
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const projectVuid = searchParams.get('project');
    const contractVuid = searchParams.get('contract');
    
    if (projectVuid) {
      // Pre-filter contracts for this project
      setProjectFilter(projectVuid);
      
      // If there's also a create parameter, open the create form
      if (searchParams.get('create') === 'true') {
        setFormData(prev => ({
          ...prev,
          project_vuid: projectVuid
        }));
        setShowCreateForm(true);
        setEditingContract(null); // Ensure we're in create mode, not edit mode
        
        // Remove the 'create' parameter from URL to prevent reopening on refresh
        const newSearchParams = new URLSearchParams(location.search);
        newSearchParams.delete('create');
        const newSearch = newSearchParams.toString();
        navigate({ search: newSearch ? `?${newSearch}` : '' }, { replace: true });
        
        // Scroll to and focus on the contract form header when creating from URL
        setTimeout(() => {
          if (contractFormHeaderRef.current) {
            contractFormHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            // Focus on the first input field (project selector)
            setTimeout(() => {
              const firstInput = contractFormHeaderRef.current?.querySelector('input[type="text"]');
              if (firstInput) {
                firstInput.focus();
              }
            }, 100);
          }
        }, 200);
      }
      
      // Refetch data with project filter
      fetchData();
      
      // Fetch project settings for allocation alerts
      fetchProjectSettings(projectVuid);
    }
  }, [location.search]);

  // Handle contract selection when URL changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const contractVuid = searchParams.get('contract');
    
    if (contractVuid) {
      // Reset ref when URL changes to allow processing new contract
      if (lastProcessedContractRef.current !== contractVuid) {
        lastProcessedContractRef.current = null;
      }
    } else {
      // No contract in URL, reset the ref
      lastProcessedContractRef.current = null;
    }
  }, [location.search]);

  // Separate effect to handle contract selection after contracts are loaded
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const contractVuid = searchParams.get('contract');
    
    if (!contractVuid) return;
    
    // Skip if we've already processed this contract
    if (lastProcessedContractRef.current === contractVuid) {
      return;
    }
    
    // Find and select the contract to view
    const contract = contracts.find(c => c.vuid === contractVuid);
    
    if (contract) {
      // Only load if not already selected to avoid re-triggering
      if (!selectedContract || selectedContract.vuid !== contractVuid) {
        lastProcessedContractRef.current = contractVuid;
        setSelectedContract(contract);
        loadContractData(contract);
      }
    } else if (contracts.length > 0) {
      // Contract not found in local state, try to fetch it directly (only once)
      lastProcessedContractRef.current = contractVuid; // Mark as processing to prevent duplicate requests
      axios.get(`${baseURL}/api/project-contracts/${contractVuid}`)
        .then(response => {
          const fetchedContract = response.data;
          if (fetchedContract) {
            // Add to contracts list if not already there
            setContracts(prev => {
              const exists = prev.find(c => c.vuid === fetchedContract.vuid);
              if (!exists) {
                return [...prev, fetchedContract];
              }
              return prev;
            });
            setSelectedContract(fetchedContract);
            loadContractData(fetchedContract);
          }
        })
        .catch(err => {
          console.error('Contract not found:', err);
          lastProcessedContractRef.current = null; // Reset on error so we can try again if URL changes
          setError(`Contract not found. The contract may have been deleted.`);
          // Clear the contract parameter from URL
          const newSearchParams = new URLSearchParams(location.search);
          newSearchParams.delete('contract');
          navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
        });
    }
  }, [location.search, contracts.length]); // Only depend on contracts.length to avoid full array dependency

  // Helper function to load contract-related data
  const loadContractData = (contract) => {
    if (!contract) return;
    
    // Fetch project-specific cost codes for this contract's project
    axios.get(`${baseURL}/api/projects/${contract.project_vuid}/cost-codes`)
      .then(response => {
        setProjectCostCodes(response.data || []);
              })
      .catch(e => {
        console.warn('Failed to fetch project cost codes:', e);
        setProjectCostCodes([]);
      });
    
    // Fetch project budget lines for this contract's project
    axios.get(`${baseURL}/api/projects/${contract.project_vuid}/budget-lines`)
      .then(response => {
        setProjectBudgetLines(response.data || []);
              })
      .catch(e => {
        console.warn('Failed to fetch project budget lines:', e);
        setProjectBudgetLines([]);
      });
    
    fetchContractItems(contract.vuid);
  };

  // Fetch project settings when project filter changes
  useEffect(() => {
    if (projectFilter && projectFilter !== 'all') {
      fetchProjectSettings(projectFilter);
    } else {
      setShowAllocationAlert(false);
      setAllocationStatus({});
    }
  }, [projectFilter]);

  // Log project cost codes when they change
  useEffect(() => {
    if (projectCostCodes.length > 0) {
          }
  }, [projectCostCodes]);

  // Fetch budget lines when project is selected in create form
  useEffect(() => {
    if (formData.project_vuid && showCreateForm) {
      let cancelled = false;
      setProjectBudgetLinesLoading(true);
      axios
        .get(`${baseURL}/api/projects/${formData.project_vuid}/budget-lines`)
        .then((res) => {
          if (cancelled) return;
          setProjectBudgetLines(Array.isArray(res.data) ? res.data : []);
        })
        .catch((err) => {
          if (cancelled) return;
          console.warn('Failed to fetch project budget lines for create form:', err);
          setProjectBudgetLines([]);
        })
        .finally(() => {
          if (!cancelled) setProjectBudgetLinesLoading(false);
        });
      return () => {
        cancelled = true;
      };
    } else if (!formData.project_vuid) {
      setProjectBudgetLines([]);
      setProjectBudgetLinesLoading(false);
    }
  }, [formData.project_vuid, showCreateForm]);

  // Focus on contract form header when creating a new contract (not editing)
  useEffect(() => {
    if (showCreateForm && !editingContract) {
      // Small delay to ensure the form is rendered
      setTimeout(() => {
        if (contractFormHeaderRef.current) {
          contractFormHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Focus on the first input field (project selector)
          setTimeout(() => {
            const firstInput = contractFormHeaderRef.current?.querySelector('input[type="text"]');
            if (firstInput) {
              firstInput.focus();
            }
          }, 100);
        }
      }, 100);
    }
  }, [showCreateForm, editingContract]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setProjectDropdownOpen(false);
        setCustomerDropdownOpen(false);
        setAccountingPeriodDropdownOpen(false);
        setContractTypeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
            
      // Check if we have a project filter from URL params
      const searchParams = new URLSearchParams(location.search);
      const projectVuid = searchParams.get('project');
      
      // Build the contracts URL with project filter if available
      let contractsUrl = `${baseURL}/api/project-contracts`;
      if (projectVuid) {
        contractsUrl += `?project_vuid=${projectVuid}`;
      }
      
      // Fetch core data first (customers endpoint is optional)
      const [
        contractsRes,
        projectsRes,
        customersRes,
        accountingPeriodsRes
      ] = await Promise.all([
        axios.get(contractsUrl),
        axios.get(`${baseURL}/api/projects`),
        axios.get(`${baseURL}/api/customers`).catch((error) => {
          if (error.response && error.response.status === 404) {
            console.warn('Customers endpoint not available, using empty list.');
            return { data: [] };
          }
          throw error;
        }),
        axios.get(`${baseURL}/api/accounting-periods/open`)
      ]);
      
            const projectList = projectsRes.data || [];
      setContracts(contractsRes.data || []);
      setProjects(projectList);
      
      let customerOptions = customersRes.data || [];
      if (!customerOptions || customerOptions.length === 0) {
        const uniqueCustomers = new Map();
        projectList.forEach((project) => {
          const baseName = project.client_name || project.project_name || 'Unknown Customer';
          const displayName = project.client_name ? baseName : `${baseName} (Project)`;
          const number = project.project_number || '';
          const key = project.client_name
            ? `${project.client_name.toLowerCase()}`
            : `project-${project.vuid}`;

          if (!uniqueCustomers.has(key)) {
            uniqueCustomers.set(key, {
              vuid: key,
              customer_name: displayName,
              customer_number: number
            });
          }
        });
        customerOptions = Array.from(uniqueCustomers.values());
      }
      setCustomers(customerOptions);
      const periodList = accountingPeriodsRes.data || [];
      setAccountingPeriods(periodList);

      try {
        let ecoUrl = `${baseURL}/api/external-change-orders`;
        if (projectVuid) {
          ecoUrl += `?project_vuid=${projectVuid}`;
        }
        const ecoRes = await axios.get(ecoUrl);
        setAllExternalChangeOrders(ecoRes.data || []);
      } catch (ecoErr) {
        console.warn('Failed to fetch external change orders:', ecoErr);
        setAllExternalChangeOrders([]);
      }

      if (!formData.accounting_period_vuid && periodList.length === 1) {
        const singlePeriod = periodList[0];
        setFormData((prev) => ({
          ...prev,
          accounting_period_vuid: singlePeriod.vuid
        }));
      }
      // Try to fetch cost codes and cost types, but don't fail if they don't work
      try {
        const costCodesRes = await axios.get(`${baseURL}/api/costcodes`);
        setCostCodes(costCodesRes.data || []);
              } catch (costCodesErr) {
        console.warn('Failed to fetch cost codes:', costCodesErr);
        setCostCodes([]);
      }
      
      try {
        const costTypesRes = await axios.get(`${baseURL}/api/costtypes`);
        setCostTypes(costTypesRes.data || []);
              } catch (costTypesErr) {
        console.warn('Failed to fetch cost types:', costTypesErr);
        setCostTypes([]);
      }
      
      // Try to fetch integrations
      try {
        const integrationsRes = await axios.get(`${baseURL}/api/integrations`);
        
        // Filter integrations to only show those that have 'contracts' enabled
        const filteredIntegrations = integrationsRes.data.filter(integration => 
          integration.enabled_objects && integration.enabled_objects.contracts === true
        );
        
        setIntegrations(filteredIntegrations || []);
              } catch (integrationsErr) {
        console.warn('Failed to fetch integrations:', integrationsErr);
        setIntegrations([]);
      }
      
      setError(null);
            
      // Safely refresh contract amounts after data is loaded
      if (contractsRes.data && contractsRes.data.length > 0) {
        setTimeout(() => {
          refreshContractAmounts().catch(err => {
            console.warn('Background contract amount refresh failed:', err);
          });
        }, 1000); // Delay by + 1 second to avoid blocking the UI
      }
    } catch (err) {
      console.error('Error fetching core data:', err);
      setError(`Failed to load data: ${err.message}`);
      // Set empty arrays as fallback
      setContracts([]);
      setProjects([]);
      setCustomers([]);
      setAccountingPeriods([]);
      setAllExternalChangeOrders([]);
      setCostCodes([]);
      setCostTypes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchProjectSettings = async (projectVuid) => {
    if (!projectVuid) return;
    
    try {
      const response = await axios.get(`${baseURL}/api/projects/${projectVuid}/settings`);
      setProjectSettings(response.data || {});
      
      // Check if we should show allocation alerts
      const shouldShowAlerts = response.data?.allocate_contract_lines_to_cost_codes || false;
      setShowAllocationAlert(shouldShowAlerts);
      
      if (shouldShowAlerts) {
        // Fetch allocation status if alerts are enabled
        await fetchAllocationStatus(projectVuid);
      }
    } catch (error) {
      console.warn('Failed to fetch project settings:', error);
      setProjectSettings({});
      setShowAllocationAlert(false);
    }
  };

  const fetchAllocationStatus = async (projectVuid) => {
    if (!projectVuid) return;
    
    try {
      const response = await axios.get(`${baseURL}/api/projects/${projectVuid}/budget-allocation-status`);
      setAllocationStatus(response.data || {});
    } catch (error) {
      console.warn('Failed to fetch allocation status:', error);
      setAllocationStatus({});
    }
  };


  const filterAndPaginateContracts = () => {
    try {
      let filtered = contracts.filter(contract => {
        const matchesSearch = !searchTerm || 
          (contract.contract_number && contract.contract_number.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (contract.description && contract.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
          (contract.customer && contract.customer.customer_name && contract.customer.customer_name.toLowerCase().includes(searchTerm.toLowerCase()));
        
        const matchesProject = projectFilter === 'all' || contract.project_vuid === projectFilter;
        const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
        
        return matchesSearch && matchesProject && matchesStatus;
      });
      
      setFilteredContracts(filtered);
    } catch (err) {
      console.error('Error filtering contracts:', err);
      setFilteredContracts([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field immediately when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleItemInputChange = (e) => {
    const { name, value } = e.target;
    setItemFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Auto-calculate total amount
    if (name === 'quantity' || name === 'unit_price') {
      const quantity = name === 'quantity' ? parseFloat(value) || 0 : parseFloat(itemFormData.quantity) || 0;
      const unitPrice = name === 'unit_price' ? parseFloat(value) || 0 : parseFloat(itemFormData.unit_price) || 0;
      setItemFormData(prev => ({
        ...prev,
        total_amount: (quantity * unitPrice).toFixed(2)
      }));
    }
    
    // Clear error for this field
    if (itemErrors[name]) {
      setItemErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const validateForm = () => {
            
    const newErrors = {};
    if (!formData.project_vuid) {
      newErrors.project_vuid = 'Project is required';
          }
    if (!formData.contract_number) {
      newErrors.contract_number = 'Contract number is required';
          }
    if (!formData.description) {
      newErrors.description = 'Description is required';
          }
    if (!formData.contract_type) {
      newErrors.contract_type = 'Contract type is required';
          }
          if (!formData.customer_vuid) {
        newErrors.customer_vuid = 'Customer is required';
              }
      if (!formData.accounting_period_vuid) {
        newErrors.accounting_period_vuid = 'Accounting period is required';
              }
    // Start and end dates are optional - removed required validation
    // if (!formData.start_date) {
    //   newErrors.start_date = 'Start date is required';
        // }
    // if (!formData.end_date) {
    //   newErrors.end_date = 'End date is required';
        // }
    
        setErrors(newErrors);
    const isValid = Object.keys(newErrors).length === 0;
        return isValid;
  };

  const validateItemForm = () => {
    const newErrors = {};
    if (!itemFormData.item_number) newErrors.item_number = 'Item number is required';
    if (!itemFormData.description) newErrors.description = 'Description is required';
    if (!itemFormData.quantity) newErrors.quantity = 'Quantity is required';
    if (!itemFormData.unit_price) newErrors.unit_price = 'Unit price is required';
    
    setItemErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
            
    if (!validateForm()) {
            return;
    }
    
        
    try {
      let response;
      // Compute amount from current items if available, otherwise 0 for new contracts
      const computedAmount = Array.isArray(contractItems) && contractItems.length > 0
        ? contractItems.reduce((sum, it) => sum + (parseFloat(it.total_amount || 0) || 0), 0)
        : 0;

      // Map frontend fields to backend expectations
      const payload = {
        project_vuid: formData.project_vuid,
        contract_number: formData.contract_number,
        contract_name: formData.description,
        contract_type: formData.contract_type,
        contract_amount: computedAmount,
        customer_vuid: formData.customer_vuid || null,
        accounting_period_vuid: formData.accounting_period_vuid,
        start_date: formData.start_date,
        end_date: formData.end_date,
        status: formData.status,
        notes: formData.notes || null,
        fee_percentage: createItemsFromBudget ? (budgetIncreasePercentage || null) : null,
      };

      
      if (editingContract) {
                response = await axios.put(`${baseURL}/api/project-contracts/${editingContract.vuid}`, payload);
        await fetchData();
        resetForm();
        setSuccessMessage('Contract updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        // Creating new contract
                response = await axios.post(`${baseURL}/api/project-contracts`, payload);
                await fetchData();
        
        // Get the newly created contract to show its items form
        const newContract = response.data;
        if (newContract && newContract.vuid) {
          // Set the newly created contract as selected and show items form
          setSelectedContract(newContract);
          setShowItemsForm(true);
          setShowCreateForm(false);
          
          // Pre-populate the item form with the new contract's VUID
          setItemFormData(prev => ({
            ...prev,
            contract_vuid: newContract.vuid,
            markup_percentage: budgetIncreasePercentage
          }));
          
          // Optionally generate items from budget lines
          if (createItemsFromBudget) {
            setCopyingBudgetLines(true);
            setBudgetLinesProgress({ current: 0, total: 0 });
            try {
              await generateItemsFromBudget(newContract);
              // Items are already added to state in generateItemsFromBudget
              // No need to refetch - just ensure contract is selected
            } finally {
              setCopyingBudgetLines(false);
              setBudgetLinesProgress({ current: 0, total: 0 });
            }
          } else {
            // Only fetch items if we didn't generate them from budget
            await fetchContractItems(newContract.vuid);
          }
          
          // Ensure the contract is still selected
          setSelectedContract(newContract);
          
          // Show success message
          setSuccessMessage(`Contract "${newContract.contract_number}" created successfully! Now add your contract items below.`);
          setError(''); // Clear any previous errors
          return; // Don't call resetForm yet, let user add contract items
        }
      }
      
      setError(''); // Clear any previous errors
    } catch (err) {
      console.error('Error saving contract:', err);
      if (err.response && err.response.data && err.response.data.error) {
        setError('Error saving contract: ' + err.response.data.error);
      } else {
        setError('Error saving contract: ' + err.message);
      }
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateItemForm()) return;
    
    // Debug logging
                
    // Check if we have a selected contract for new items
    if (!editingItem && !selectedContract) {
            setError('No contract selected. Please view contract items first.');
      return;
    }
    
    try {
      let savedItem;
      const sanitizedFormData = {
        ...itemFormData,
        markup_percentage: parseFloat(itemFormData.markup_percentage) || 0,
        apply_fee_to_labor_schedule: Boolean(itemFormData.apply_fee_to_labor_schedule)
      };
      if (editingItem) {
                const response = await axios.put(`${baseURL}/api/project-contract-items/${editingItem.vuid}`, sanitizedFormData);
        savedItem = response.data;
        setContractItems(prev => prev.map(item => item.vuid === editingItem.vuid ? savedItem : item));
        
        // Refresh contract data to update total amount
        const contractResponse = await axios.get(`${baseURL}/api/project-contracts/${editingItem.contract_vuid}`);
        const updatedContract = contractResponse.data;
        setContracts(prev => prev.map(c => c.vuid === editingItem.contract_vuid ? updatedContract : c));
        if (selectedContract && selectedContract.vuid === editingItem.contract_vuid) {
          setSelectedContract(updatedContract);
        }
      } else {
                
        // Clean the data - convert empty strings to null for optional fields
        const cleanedData = {
          ...sanitizedFormData,
          contract_vuid: selectedContract.vuid,
          cost_code_vuid: itemFormData.cost_code_vuid || null,
          cost_type_vuid: itemFormData.cost_type_vuid || null,
          specifications: itemFormData.specifications || null,
          delivery_location: itemFormData.delivery_location || null,
          delivery_date: itemFormData.delivery_date || null,
          warranty_info: itemFormData.warranty_info || null,
          notes: itemFormData.notes || null
        };
        
        const requestData = cleanedData;
                        const response = await axios.post(`${baseURL}/api/project-contract-items`, requestData);
        savedItem = response.data;
        
        setContractItems(prev => [...prev, savedItem]);
        
        // Refresh contract data to update total amount
        const contractResponse = await axios.get(`${baseURL}/api/project-contracts/${selectedContract.vuid}`);
        const updatedContract = contractResponse.data;
        setContracts(prev => prev.map(c => c.vuid === selectedContract.vuid ? updatedContract : c));
        setSelectedContract(updatedContract);
      }
      
      // Only show success message if API call succeeded
      setSuccessMessage('Item saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      resetItemForm();
      setEditingItem(null);
    } catch (err) {
      console.error('Error saving item:', err);
      console.error('Error response:', err.response);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      const errorMessage = err.response?.data?.error || err.message || 'Unknown error';
      if (errorMessage.includes('accounting period is closed')) {
        setError(`Cannot save item: The accounting period for this contract is closed. Please create a change order to add new items.`);
      } else {
        setError(`Error saving item: ${errorMessage}`);
      }
    }
  };

  const handleEditItem = (item) => {
    setEditingItem(item);
    setItemFormData({
      item_number: item.item_number || '',
      description: item.description || '',
      quantity: item.quantity || 1,
      unit_of_measure: item.unit_of_measure || 'EA',
      unit_price: item.unit_price || '',
      total_amount: item.total_amount || '',
      markup_percentage: item.markup_percentage ?? budgetIncreasePercentage,
      status: item.status || 'active',
      cost_code_vuid: item.cost_code_vuid || null,
      cost_type_vuid: item.cost_type_vuid || null,
      specifications: item.specifications || '',
      delivery_location: item.delivery_location || '',
      delivery_date: item.delivery_date || '',
      warranty_info: item.warranty_info || '',
      notes: item.notes || '',
      contract_vuid: item.contract_vuid || (selectedContract ? selectedContract.vuid : '')
    });
    setShowItemsForm(true);
  };

  const handleDeleteItem = async (itemVuid) => {
    if (itemVuid.startsWith('temp-')) {
      // Remove temporary item from local state
      setContractItems(prev => prev.filter(item => item.vuid !== itemVuid));
    } else {
      // Delete existing item from backend
      try {
        await axios.delete(`${baseURL}/api/project-contract-items/${itemVuid}`);
        await fetchContractItems(selectedContract.vuid);
        setSuccessMessage('Contract item deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        setError('Error deleting contract item: ' + err.message);
      }
    }
  };

  // Handle manage allocations
  const handleManageAllocations = (item) => {
    setSelectedItemForAllocations(item);
    const cachedAllocations = allocationsByItem[item.vuid] || [];
    setAllocations(cachedAllocations);
    setShowAllocationsModal(true);

    const contract = contracts.find(c => c.vuid === item.contract_vuid);
    if (contract && selectedContract?.vuid !== contract.vuid) {
      setSelectedContract(contract);
    }

    const projectVuid = contract?.project_vuid || selectedContract?.project_vuid;

    if (item.vuid && !item.vuid.startsWith('temp-')) {
      axios.get(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`)
        .then((res) => {
          const fetched = res.data || [];
          setAllocations(fetched);
          setAllocationsByItem((prev) => ({ ...prev, [item.vuid]: fetched }));
        })
        .catch((err) => {
          console.error('Error fetching allocations:', err);
        });
    }

    if (projectVuid && projectBudgetLines.length === 0) {
      axios
        .get(`${baseURL}/api/projects/${projectVuid}/budget-lines`)
        .then((res) => {
          setProjectBudgetLines(Array.isArray(res.data) ? res.data : []);
        })
        .catch((err) => {
          console.warn('Failed to fetch project budget lines:', err);
        });
    }

    if (!projectVuid) return;
    axios.get(`${baseURL}/api/accounting-periods/open`)
      .then((openPeriodsRes) => {
        const openPeriods = openPeriodsRes.data || [];
        const openPeriod = openPeriods.length === 1 ? openPeriods[0] : null;
        const params = { project_vuid: projectVuid };
        if (openPeriod) params.accounting_period_vuid = openPeriod.vuid;
        return axios.get(`${baseURL}/api/project-billings/unallocated-costs`, { params });
      })
      .then((unallocatedRes) => {
        if (unallocatedRes.data?.success && unallocatedRes.data?.unallocated_costs) {
          setUnallocatedCosts(unallocatedRes.data.unallocated_costs || []);
        }
      })
      .catch((err) => {
        console.warn('Failed to fetch unallocated costs:', err);
      });
  };

  // Handle add allocation
  const handleAddAllocation = () => {
    if (newAllocation.cost_code_vuid && newAllocation.cost_type_vuid) {
      const allocation = {
        ...newAllocation,
        vuid: `temp-allocation-${Date.now()}`,
        contract_item_vuid: selectedItemForAllocations.vuid
      };
      setAllocations([...allocations, allocation]);
      setNewAllocation({
        cost_code_vuid: '',
        cost_type_vuid: '',
      });
      setShowAddAllocationForm(false);
    }
  };

  // Handle delete allocation
  const handleDeleteAllocation = async (allocationVuid) => {
    try {
      // If this is an existing allocation (has a real vuid), delete it from the database
      if (allocationVuid && !allocationVuid.startsWith('temp-')) {
        await axios.delete(`${baseURL}/api/project-contract-items/allocations/${allocationVuid}`);
              }
      
      // Remove from local state
      setAllocations(allocations.filter(a => a.vuid !== allocationVuid));
      
      // Refresh allocation status to update the alert
      const projectVuid = selectedContract?.project_vuid;
      if (projectVuid) {
        await fetchAllocationStatus(projectVuid);
      }
    } catch (error) {
      console.error('Error deleting allocation:', error);
      setError('Error deleting allocation: ' + error.message);
    }
  };

  // Handle save allocations
  const handleSaveAllocations = async () => {
    try {
      const item = selectedItemForAllocations;
      if (!item) return;
      
                        
      if (item.vuid && !item.vuid.startsWith('temp-')) {
        // Validate all allocations before making any changes
                for (const alloc of allocations) {
          const validation = validateCostCodeTypeCombination(alloc.cost_code_vuid, alloc.cost_type_vuid, item.vuid);
          if (!validation.isValid) {
            throw new Error(`Allocation validation failed: ${validation.message}`);
          }
        }
                
        // Get current allocations from backend to compare
        const existingRes = await axios.get(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`);
        const existingAllocs = existingRes.data || [];
                
        // Check if allocations actually changed to avoid unnecessary operations
        const allocationsChanged = allocations.length !== existingAllocs.length || 
          allocations.some((alloc, index) => {
            const existing = existingAllocs[index];
            return !existing || 
                   existing.cost_code_vuid !== alloc.cost_code_vuid || 
                   existing.cost_type_vuid !== alloc.cost_type_vuid;
          });
        
        if (!allocationsChanged) {
                    setShowAllocationsModal(false);
          setSuccessMessage('No changes to save');
          setTimeout(() => setSuccessMessage(''), 3000);
          return;
        }
        
        // Only create new allocations (deletions are handled by handleDeleteAllocation)
                const createdAllocs = [];
        for (const alloc of allocations) {
          // Only create if it's a new allocation (doesn't have a real vuid yet)
          if (!alloc.vuid || alloc.vuid.startsWith('temp-')) {
                        const response = await axios.post(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`, {
              cost_code_vuid: alloc.cost_code_vuid,
              cost_type_vuid: alloc.cost_type_vuid,
              notes: alloc.notes || ''
            });
            createdAllocs.push(response.data);
                      } else {
            // Keep existing allocation
            createdAllocs.push(alloc);
                      }
        }
        
        // Update local state with the newly created allocations
                setAllocations(createdAllocs);
        setAllocationsByItem(prev => ({ ...prev, [item.vuid]: createdAllocs }));
        
        // Refresh the contract items to ensure consistency
        await fetchContractItems(selectedContract.vuid);
        
        // Refresh allocation status to update the alert
        const projectVuid = selectedContract?.project_vuid;
        if (projectVuid) {
          await fetchAllocationStatus(projectVuid);
        }
        
      } else {
        // Cache locally for temp items
                setAllocationsByItem(prev => ({ ...prev, [item.vuid]: allocations }));
      }
      
      setShowAllocationsModal(false);
      setSuccessMessage('Allocations saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
    } catch (err) {
      console.error('Error in handleSaveAllocations:', err);
      setError('Error saving allocations: ' + err.message);
    }
  };

  // Helper function to get all allocations for a contract
  const getAllContractAllocations = (contract) => {
    if (!contract?.items) return [];
    return contract.items.flatMap(item => allocationsByItem[item.vuid] || []);
  };

  // Helper function to validate cost code cost type combination uniqueness
  const validateCostCodeTypeCombination = (costCodeVuid, costTypeVuid, currentItemVuid = null) => {
    if (!costCodeVuid || !costTypeVuid) return { isValid: true, message: '' };
    
                    
    // Check against existing contract items first (more reliable)
    // IMPORTANT: Only check against items that are NOT temp items and NOT the current item being validated
    const existingItems = contractItems.filter(item => 
      item.vuid && 
      !item.vuid.startsWith('temp-') && 
      item.vuid !== currentItemVuid &&
      item.cost_code_vuid && 
      item.cost_type_vuid
    );
    
        
    // Check if any existing item has the same cost code cost type combination
    const duplicateItem = existingItems.find(item => 
      item.cost_code_vuid === costCodeVuid && item.cost_type_vuid === costTypeVuid
    );
    
    if (duplicateItem) {
      const costCode = costCodes.find(c => c.vuid === costCodeVuid);
      const costType = costTypes.find(t => t.vuid === costTypeVuid);
      const codeName = costCode ? costCode.code : costCodeVuid;
      const typeName = costType ? costType.cost_type : costTypeVuid;
      
            
      return {
        isValid: false,
        message: `Cost code ${codeName} with cost type ${typeName} is already used by item ${duplicateItem.item_number}.`
      };
    }
    
    // Also check against allocations as a backup
    const allContractAllocations = getAllContractAllocations(selectedContract);
        
    const duplicateAllocation = allContractAllocations.find(allocation => {
      // Skip the current item if we're editing
      if (currentItemVuid && allocation.contract_item_vuid === currentItemVuid) {
        return false;
      }
      
      return allocation.cost_code_vuid === costCodeVuid && 
             allocation.cost_type_vuid === costTypeVuid;
    });
    
    if (duplicateAllocation) {
      const costCode = costCodes.find(c => c.vuid === costCodeVuid);
      const costType = costTypes.find(t => t.vuid === costTypeVuid);
      const codeName = costCode ? costCode.code : costCodeVuid;
      const typeName = costType ? costType.cost_type : costTypeVuid;
      
            
      return {
        isValid: false,
        message: `Cost code ${codeName} with cost type ${typeName} is already allocated to another contract item.`
      };
    }
    
        return { isValid: true, message: '' };
  };

  // Helper function to get all available cost codes (global project-specific)
  const getAllCostCodes = () => {
    return [...costCodes, ...projectCostCodes];
  };

  const contractCostCodeOptions = () => {
    const useProjectCodes = projectSettings?.allow_project_cost_codes === true;
    const byVuid = new Map();
    const add = (code) => {
      if (code?.vuid) byVuid.set(code.vuid, code);
    };
    if (useProjectCodes) {
      projectCostCodes
        .filter((code) => code.is_project_specific || code.source === 'project')
        .forEach(add);
    } else {
      getAllCostCodes().forEach(add);
    }
    projectBudgetLines.forEach((line) => {
      if (!line.cost_code?.vuid || byVuid.has(line.cost_code.vuid)) return;
      if (useProjectCodes && costCodes.some((code) => code.vuid === line.cost_code.vuid)) return;
      add(line.cost_code);
    });
    return [...byVuid.values()]
      .filter(isCostCodeSelectable)
      .sort((a, b) => String(a.code || '').localeCompare(String(b.code || '')));
  };

  const contractCostTypeOptions = (costCodeVuid, selectedTypeVuid) => {
    const options = costTypes.filter(isCostTypeSelectable).filter((costType) => {
      const typeName = String(costType.cost_type || '').trim().toLowerCase();
      const typeAbbr = String(costType.abbreviation || '').trim().toLowerCase();
      if (typeAbbr === 'f' || typeName === 'fee' || typeName === 'fees' || typeName.startsWith('fee ')) {
        return true;
      }
      if (!projectBudgetLines.length) return true;
      if (!costCodeVuid) {
        return projectBudgetLines.some((line) => line.cost_type_vuid === costType.vuid);
      }
      return projectBudgetLines.some((line) =>
        line.cost_type_vuid === costType.vuid && line.cost_code_vuid === costCodeVuid
      );
    });
    if (selectedTypeVuid && !options.some((costType) => costType.vuid === selectedTypeVuid)) {
      const current = costTypes.find((costType) => costType.vuid === selectedTypeVuid);
      if (current) options.unshift(current);
    }
    return options;
  };

  // Helper function to get the display value for cost code input
  const getCostCodeInputValue = (item) => {
    if (!item.cost_code_vuid) return '';
    if (typeof item.cost_code_vuid === 'string' && item.cost_code_vuid.length !== 36) {
      return item.cost_code_vuid;
    }
    const costCode = getAllCostCodes().find((c) => c.vuid === item.cost_code_vuid);
    return costCode ? costCode.code : '';
  };

  /** Global cost codes from API often omit status; treat missing status as active. */
  const isCostCodeSelectable = (c) => !c.status || c.status === 'active';
  const isCostTypeSelectable = (t) => !t.status || t.status === 'active';

  // Function to create project-specific cost code if it doesn't exist (similar to budget entries)
  const createProjectCostCodeIfNeeded = async (costCodeValue) => {
    if (!projectSettings?.allow_project_cost_codes) {
      return costCodeValue; // Return as-is for global mode
    }

    // Check if this cost code already exists as a project cost code
    const existingProjectCostCode = projectCostCodes.find(
      cc => cc.is_project_specific && cc.code === costCodeValue
    );

    if (existingProjectCostCode) {
      return existingProjectCostCode.vuid; // Return existing VUID
    }

    // Create new project-specific cost code
    try {
      const response = await axios.post(`${baseURL}/api/projects/${selectedContract.project_vuid}/cost-codes`, {
        code: costCodeValue,
        description: `Project-specific cost code: ${costCodeValue}`
      });
      
      // Add to local state
      const newCostCode = response.data;
      setProjectCostCodes(prev => [...prev, newCostCode]);
      
      return newCostCode.vuid;
    } catch (error) {
      console.error('Error creating project cost code:', error);
      throw error;
    }
  };

  // Helper functions for smart dropdowns
  const getFilteredProjects = () => {
    if (!projectSearchTerm) return projects;
    return projects.filter(project => 
      project.project_name.toLowerCase().includes(projectSearchTerm.toLowerCase()) ||
      project.project_number.toLowerCase().includes(projectSearchTerm.toLowerCase())
    );
  };

  const getFilteredCustomers = () => {
    if (!customerSearchTerm) return customers;
    const searchLower = customerSearchTerm.toLowerCase();
    return customers.filter(customer => 
      (customer.customer_name || '').toLowerCase().includes(searchLower) ||
      (customer.customer_number || '').toLowerCase().includes(searchLower)
    );
  };

  // Helper functions to get display values for cost code and cost type columns
  const getCostCodeDisplay = (item) => {
    const itemAllocs = allocationsByItem[item.vuid] || [];
        
    // If there are multiple allocations, always show "Multiple"
    if (itemAllocs.length > 1) {
      return 'Multiple';
    }
    
    // If there's exactly one allocation, show that allocation's cost code
    if (itemAllocs.length === 1) {
      // Check both global and project-specific cost codes
      let costCode = costCodes.find(c => c.vuid === itemAllocs[0].cost_code_vuid);
      if (!costCode) {
        costCode = projectCostCodes.find(c => c.vuid === itemAllocs[0].cost_code_vuid);
      }
      return costCode ? `${costCode.code} - ${costCode.description}` : 'Unknown';
    }
    
    // If no allocations, show the item's own cost code (if any)
    if (item.cost_code_vuid) {
      // Check both global and project-specific cost codes
      let costCode = costCodes.find(c => c.vuid === item.cost_code_vuid);
      if (!costCode) {
        costCode = projectCostCodes.find(c => c.vuid === item.cost_code_vuid);
      }
      return costCode ? `${costCode.code} - ${costCode.description}` : 'Unknown';
    }
    
    return '';
  };

  const getCostTypeDisplay = (item) => {
    const itemAllocs = allocationsByItem[item.vuid] || [];
        
    // If there are multiple allocations, always show "Multiple"
    if (itemAllocs.length > 1) {
      return 'Multiple';
    }
    
    // If there's exactly one allocation, show that allocation's cost type
    if (itemAllocs.length === 1) {
      const costType = costTypes.find(t => t.vuid === itemAllocs[0].cost_type_vuid);
      return costType ? costType.cost_type : 'Unknown';
    }
    
    // If no allocations, show the item's own cost type (if any)
    if (item.cost_type_vuid) {
      const costType = costTypes.find(t => t.vuid === item.cost_type_vuid);
      return costType ? costType.cost_type : 'Unknown';
    }
    
    return '';
  };

  // Helper function to get markup from fee settings
  const getMarkupFromFeeSettings = (costCodeVuid, costTypeVuid) => {
    if (!costCodeVuid || !costTypeVuid || !usesLineMarkups(feeLevel)) {
      return null;
    }
    
    const feeSetting = feeSettings.find(
      setting => setting.cost_code_vuid === costCodeVuid && 
                 setting.cost_type_vuid === costTypeVuid
    );
    
    return feeSetting ? (feeSetting.fee_percentage || 0) : null;
  };

  const itemMarkupValue = (item) => {
    const fromSettings = getMarkupFromFeeSettings(item.cost_code_vuid, item.cost_type_vuid);
    return fromSettings !== null ? fromSettings : (item.markup_percentage ?? 0);
  };

  const saveInlineMarkup = async (item, rawValue) => {
    const feePercentage = rawValue === '' ? 0 : parseFloat(rawValue);
    if (!item?.cost_code_vuid || !item?.cost_type_vuid || Number.isNaN(feePercentage)) {
      setMarkupDrafts((prev) => {
        const next = { ...prev };
        delete next[item.vuid];
        return next;
      });
      return;
    }
    const current = parseFloat(itemMarkupValue(item)) || 0;
    if (Math.abs(current - feePercentage) < 0.001) {
      setMarkupDrafts((prev) => {
        const next = { ...prev };
        delete next[item.vuid];
        return next;
      });
      return;
    }
    try {
      const setting = feeSettings.find((row) => (
        row.cost_code_vuid === item.cost_code_vuid && row.cost_type_vuid === item.cost_type_vuid
      ));
      const response = setting?.vuid
        ? await axios.put(`${baseURL}/api/contracts/${selectedContract.vuid}/fee-settings/${setting.vuid}`, {
          fee_percentage: feePercentage
        })
        : await axios.post(`${baseURL}/api/contracts/${selectedContract.vuid}/fee-settings`, {
          cost_code_vuid: item.cost_code_vuid,
          cost_type_vuid: item.cost_type_vuid,
          fee_percentage: feePercentage,
          default_fee_percentage: setting?.default_fee_percentage || 0
        });
      const saved = response.data || {};
      setFeeSettings((prev) => {
        const exists = prev.some((row) => (
          row.cost_code_vuid === item.cost_code_vuid && row.cost_type_vuid === item.cost_type_vuid
        ));
        if (!exists) {
          return [...prev, { ...saved, fee_percentage: feePercentage, cost_code_vuid: item.cost_code_vuid, cost_type_vuid: item.cost_type_vuid }];
        }
        return prev.map((row) => (
          row.cost_code_vuid === item.cost_code_vuid && row.cost_type_vuid === item.cost_type_vuid
            ? { ...row, ...saved, fee_percentage: feePercentage }
            : row
        ));
      });
      const updatedItems = contractItems.map((row) => {
        if (row.cost_code_vuid !== item.cost_code_vuid || row.cost_type_vuid !== item.cost_type_vuid) {
          return row;
        }
        const total = recalculateTotalAmountWithNewMarkup(
          parseFloat(row.total_amount || 0),
          current,
          feePercentage
        );
        const quantity = parseFloat(row.quantity || 0);
        return {
          ...row,
          markup_percentage: feePercentage,
          total_amount: total,
          unit_price: quantity ? Math.round((total / quantity) * 100) / 100 : total,
        };
      });
      setContractItems(updatedItems);
      const changed = updatedItems.filter((row) => (
        row.cost_code_vuid === item.cost_code_vuid
        && row.cost_type_vuid === item.cost_type_vuid
        && row.vuid
        && !String(row.vuid).startsWith('temp-')
      ));
      await Promise.all(changed.map((row) => axios.put(`${baseURL}/api/project-contract-items/${row.vuid}`, {
        total_amount: row.total_amount,
        unit_price: row.unit_price,
        markup_percentage: row.markup_percentage,
      })));
      if (selectedContract?.vuid) {
        const contractTotal = Math.round(updatedItems.reduce(
          (sum, row) => sum + (parseFloat(row.total_amount || 0) || 0),
          0
        ) * 100) / 100;
        setContracts((prev) => prev.map((contract) => (
          contract.vuid === selectedContract.vuid ? { ...contract, contract_amount: contractTotal } : contract
        )));
        setSelectedContract((prev) => (prev ? { ...prev, contract_amount: contractTotal } : null));
        axios.put(`${baseURL}/api/project-contracts/${selectedContract.vuid}`, {
          contract_amount: contractTotal,
        }).catch((err) => {
          console.warn('Error updating contract amount:', err);
        });
      }
      setMarkupDrafts((prev) => {
        const next = { ...prev };
        delete next[item.vuid];
        return next;
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save markup');
    }
  };

  // Helper function to recalculate total_amount based on markup change
  // If we have an old markup and new markup, recalculate total_amount
  // Formula: new_total = (old_total / (1 + old_markup/100)) * (1 + new_markup/100)
  const recalculateTotalAmountWithNewMarkup = (currentTotal, oldMarkup, newMarkup) => {
    if (!currentTotal || currentTotal <= 0) {
      return currentTotal;
    }
    
    const oldMarkupValue = parseFloat(oldMarkup || 0);
    const newMarkupValue = parseFloat(newMarkup || 0);
    
    // If markup hasn't changed, return current total
    if (oldMarkupValue === newMarkupValue) {
      return currentTotal;
    }
    
    // Calculate base amount (what total_amount would be without markup)
    // If old markup was applied: base = total / (1 + old_markup/100)
    const baseAmount = oldMarkupValue > 0 
      ? parseFloat(currentTotal) / (1 + (oldMarkupValue / 100))
      : parseFloat(currentTotal);
    
    // Apply new markup: new_total = base * (1 + new_markup/100)
    const newTotal = baseAmount * (1 + (newMarkupValue / 100));
    return Math.round(newTotal * 100) / 100; // Round to 2 decimals
  };

  // Handle view items
  const handleViewItems = async (contract) => {
        setContractItemsLoading(true);
        setContractItems([]);
        setSelectedContract(contract);
    setShowItemsForm(true);
    setShowCreateForm(false);
    
    // Reset percentage adjustment states when viewing a different contract
    setAdjustmentPercentage(0);
    setShowPercentageAdjustment(false);
    setOriginalAmounts({});
    
    // Clear existing project cost codes first
    setProjectCostCodes([]);

    const projectVuid = contract.project_vuid;
    const itemsPromise = fetchContractItems(contract.vuid, 'fee_cost_code', []);
    const [costCodesResult, budgetResult, settingsResult, laborResult, feeLevelResult] = await Promise.all([
      axios.get(`${baseURL}/api/projects/${projectVuid}/cost-codes`).then((response) => response.data || []).catch((e) => {
        console.error('Failed to fetch project cost codes:', e);
        return [];
      }),
      axios.get(`${baseURL}/api/projects/${projectVuid}/budget-lines`).then((response) => response.data || []).catch((e) => {
        console.warn('Failed to fetch project budget lines:', e);
        return [];
      }),
      axios.get(`${baseURL}/api/projects/${projectVuid}/settings`).then((response) => response.data || {}).catch((e) => {
        console.warn('Failed to fetch project settings:', e);
        return {};
      }),
      axios.get(`${baseURL}/api/labor-rate-schedules`, { params: { project_vuid: projectVuid } }).then((response) => response.data || []).catch((e) => {
        console.warn('Failed to fetch labor rate schedules:', e);
        return [];
      }),
      axios.get(`${baseURL}/api/contracts/${contract.vuid}/fee-level`).then((response) => response.data?.fee_level || 'contract_item').catch((e) => {
        console.warn('Failed to fetch fee level:', e);
        return 'contract_item';
      }),
    ]);

    setProjectCostCodes(costCodesResult);
    setProjectBudgetLines(budgetResult);
    setProjectSettings(settingsResult);
    const laborCodes = new Set();
    (laborResult || []).forEach((schedule) => {
      (schedule.lines || []).forEach((line) => {
        if (line.cost_code_vuid && (line.status || 'active') === 'active') {
          laborCodes.add(line.cost_code_vuid);
        }
      });
    });
    setLaborScheduleCostCodes(laborCodes);

    const currentFeeLevel = feeLevelResult || 'contract_item';
    setFeeLevel(currentFeeLevel);

    let currentFeeSettings = [];
    if (usesLineMarkups(currentFeeLevel)) {
      try {
        const feeSettingsRes = await axios.get(`${baseURL}/api/contracts/${contract.vuid}/fee-settings`);
        currentFeeSettings = feeSettingsRes.data || [];
        setFeeSettings(currentFeeSettings);
      } catch (e) {
        console.warn('Failed to fetch fee settings:', e);
        setFeeSettings([]);
      }
    } else {
      setFeeSettings([]);
    }

    const loadedItems = await itemsPromise;
    if (usesLineMarkups(currentFeeLevel) && currentFeeSettings.length > 0 && loadedItems.length > 0) {
      const withMarkups = applyLineFeeMarkups(loadedItems, currentFeeLevel, currentFeeSettings);
      setContractItems(withMarkups);
      saveRecalculatedItemMarkups(loadedItems, withMarkups);
    }
  };

  // Handle hide items
  const handleHideItems = () => {
    setSelectedContract(null);
    setShowItemsForm(false);
    setContractItems([]);
    setContractItemsLoading(false);
  };

  const applyLineFeeMarkups = (items, effectiveFeeLevel, effectiveFeeSettings) => {
    if (!usesLineMarkups(effectiveFeeLevel) || !items.length || !effectiveFeeSettings.length) {
      return items;
    }
    const getMarkupFromSettings = (costCodeVuid, costTypeVuid) => {
      const feeSetting = effectiveFeeSettings.find(
        setting => setting.cost_code_vuid === costCodeVuid &&
                   setting.cost_type_vuid === costTypeVuid
      );
      return feeSetting ? (feeSetting.fee_percentage || 0) : null;
    };
    return items.map(item => {
      if (!item.cost_code_vuid || !item.cost_type_vuid) {
        return item;
      }
      const markupFromSettings = getMarkupFromSettings(item.cost_code_vuid, item.cost_type_vuid);
      if (markupFromSettings === null) {
        return item;
      }
      const oldMarkup = item.markup_percentage ?? 0;
      const newMarkup = markupFromSettings;
      const currentTotal = parseFloat(item.total_amount || 0);
      const total = recalculateTotalAmountWithNewMarkup(currentTotal, oldMarkup, newMarkup);
      const quantity = parseFloat(item.quantity || 0);
      return {
        ...item,
        markup_percentage: newMarkup,
        total_amount: total,
        unit_price: quantity ? Math.round((total / quantity) * 100) / 100 : total,
      };
    });
  };

  const saveRecalculatedItemMarkups = (originalItems, itemsToSet) => {
    const originalItemsMap = new Map((originalItems || []).map(item => [item.vuid, item]));
    const itemsNeedingUpdate = itemsToSet.filter(item => {
      if (!item.cost_code_vuid || !item.cost_type_vuid || !item.vuid || item.vuid.startsWith('temp-')) {
        return false;
      }
      const originalItem = originalItemsMap.get(item.vuid);
      if (!originalItem) {
        return false;
      }
      const originalTotal = parseFloat(originalItem.total_amount || 0);
      const newTotal = parseFloat(item.total_amount || 0);
      const originalMarkup = parseFloat(originalItem.markup_percentage || 0);
      const newMarkup = parseFloat(item.markup_percentage || 0);
      return Math.abs(originalTotal - newTotal) > 0.01 || Math.abs(originalMarkup - newMarkup) > 0.01;
    });
    if (itemsNeedingUpdate.length === 0) {
      return;
    }
    Promise.all(itemsNeedingUpdate.map(async (item) => {
      try {
        await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
          total_amount: item.total_amount,
          unit_price: item.unit_price,
          markup_percentage: item.markup_percentage
        });
      } catch (err) {
        console.warn(`Failed to update item ${item.item_number}:`, err);
      }
    }));
  };

  // Fetch contract items for a specific contract
  const fetchContractItems = async (contractVuid, feeLevelParam = null, feeSettingsParam = null) => {
    try {
            const response = await axios.get(`${baseURL}/api/project-contracts/${contractVuid}/items`);
                        
      // Use passed parameters or state (parameters take precedence for immediate updates)
      const effectiveFeeLevel = feeLevelParam !== null ? feeLevelParam : feeLevel;
      const effectiveFeeSettings = feeSettingsParam !== null ? feeSettingsParam : feeSettings;
      
      let itemsToSet = applyLineFeeMarkups(response.data || [], effectiveFeeLevel, effectiveFeeSettings);
      
      // Log each item to see what we're getting
      if (itemsToSet && itemsToSet.length > 0) {
        itemsToSet.forEach((item, index) => {
                  });
      } else {
        console.warn('⚠️ No items in response data');
      }
      
            savedContractItemsRef.current = new Map(
              (itemsToSet || [])
                .filter((item) => item?.vuid)
                .map((item) => [item.vuid, item])
            );
            setContractItems((current) => {
              const pending = (current || []).filter((item) => String(item.vuid || '').startsWith('temp-'));
              return pending.length ? [...itemsToSet, ...pending] : itemsToSet;
            });
            setContractItemsLoading(false);
            const allocationsMap = {};
            (itemsToSet || []).forEach((item) => {
              if (item.vuid && !String(item.vuid).startsWith('temp-')) {
                allocationsMap[item.vuid] = item.allocations || [];
              }
            });
            setAllocationsByItem((prev) => ({ ...prev, ...allocationsMap }));
            
      // If fee level is contract_item and we recalculated items, save them back to database
      // This ensures the database has the correct total_amount values with markup applied
      if (usesLineMarkups(effectiveFeeLevel) && itemsToSet.length > 0 && effectiveFeeSettings.length > 0) {
        saveRecalculatedItemMarkups(response.data || [], itemsToSet);
      }
      
      // Also fetch change order lines for this contract
      fetchChangeOrderLines(contractVuid);
      
      // Update contract amount based on fetched items (use itemsToSet which may have recalculated totals)
      if (itemsToSet && itemsToSet.length > 0) {
        // Sum all item amounts and round to 2 decimal places to avoid floating point precision issues
        const total = Math.round(itemsToSet.reduce((sum, item) => sum + (parseFloat(item.total_amount || 0) || 0), 0) * 100) / 100;
        
        // Update local contract state
        setContracts(prev => prev.map(c => 
          c.vuid === contractVuid ? { ...c, contract_amount: total } : c
        ));
        
        // Update selected contract if it's the current one
        if (selectedContract && selectedContract.vuid === contractVuid) {
          setSelectedContract(prev => prev ? { ...prev, contract_amount: total } : null);
        }
        
        // Update the contract amount in the backend (non-blocking)
        // This ensures the database has the correct calculated amount from items
        // Don't call fetchData() here as it may clear the selected contract and items
        axios.put(`${baseURL}/api/project-contracts/${contractVuid}`, {
          contract_amount: total
        }).then(() => {
                    // Just update the local contracts list without refetching everything
          // This preserves the selected contract and items
        }).catch(err => {
          console.warn('⚠️ Error updating contract amount:', err);
        });
      }
      return itemsToSet || [];
    } catch (err) {
      console.error('❌ Error fetching contract items:', err);
      console.error('❌ Error response:', err.response?.data);
      console.error('❌ Error status:', err.response?.status);
      // Don't clear items on error - keep existing items visible
      // Only clear if this is a 404 (contract not found) or 401 (unauthorized)
      if (err.response?.status === 404 || err.response?.status === 401) {
        console.warn('⚠️ Clearing items due to 404/401 error');
        setContractItems([]);
      } else {
        console.warn('⚠️ Keeping existing items despite fetch error');
      }
      return [];
    } finally {
      setContractItemsLoading(false);
    }
  };

  // Fetch change order lines for a specific contract
  const fetchChangeOrderLines = async (contractVuid) => {
    try {
            
      // First get all external change orders for this contract
      const changeOrdersResponse = await axios.get(`${baseURL}/api/external-change-orders?contract_vuid=${contractVuid}`);
      const changeOrders = changeOrdersResponse.data || [];
      setExternalChangeOrders(changeOrders);
      
            
      // Then get all change order lines for these change orders
      const allChangeOrderLines = [];
      for (const changeOrder of changeOrders) {
        try {
          const linesResponse = await axios.get(`${baseURL}/api/external-change-orders/${changeOrder.vuid}/lines`);
          const lines = linesResponse.data || [];
          // Add change order info to each line
          const linesWithChangeOrder = lines.map(line => ({
            ...line,
            change_order_number: changeOrder.change_order_number,
            change_order_date: changeOrder.change_order_date,
            change_order_status: changeOrder.status
          }));
          allChangeOrderLines.push(...linesWithChangeOrder);
        } catch (err) {
          console.error(`Error fetching lines for change order ${changeOrder.vuid}:`, err);
        }
      }
      
      setChangeOrderLines(allChangeOrderLines);
            
    } catch (err) {
      console.error('Error fetching change order lines:', err);
      setChangeOrderLines([]);
      setExternalChangeOrders([]);
    }
  };

  // Calculate change order amount for a specific contract line
  const getChangeOrderAmount = (contractLine) => {
    if (!contractLine.cost_code_vuid || !contractLine.cost_type_vuid) {
      return + 0;
    }
    
    const matchingChangeOrderLines = changeOrderLines.filter(changeLine => 
      changeLine.cost_code_vuid === contractLine.cost_code_vuid &&
      changeLine.cost_type_vuid === contractLine.cost_type_vuid
    );
    
    const totalChangeAmount = matchingChangeOrderLines.reduce((sum, changeLine) => {
      return sum + (parseFloat(changeLine.contract_amount_change || 0) || 0);
    }, 0);
    
    return totalChangeAmount;
  };

  const showContractChangeColumns = (contractItems || []).some((item) => getChangeOrderAmount(item) !== 0);
  const showContractNotesColumn = notesColumnOpen
    || String(newItemData.notes || '').trim() !== ''
    || (contractItems || []).some((item) => String(item.notes || '').trim() !== '');
  const contractItemColumnCount = 6
    + (usesLineMarkups(feeLevel) ? 2 : 0)
    + (showContractChangeColumns ? 2 : 0)
    + (showContractNotesColumn ? 1 : 0);

  const getContractChangeOrderTotal = (contractVuid) => {
    if (!contractVuid) return 0;
    return (allExternalChangeOrders || [])
      .filter(
        (co) =>
          co.contract_vuid === contractVuid &&
          String(co.status || '').toLowerCase() === 'approved'
      )
      .reduce((sum, co) => sum + (parseFloat(co.total_contract_change_amount) || 0), 0);
  };

  // Reset forms
  const resetForm = () => {
    setFormData({
      project_vuid: '',
      contract_number: '',
      contract_date: '',
      customer_vuid: '',
      accounting_period_vuid: '',
      contract_type: CONTRACT_TYPES[0].value,
      status: 'active',
      description: '',
      total_amount: '',
      currency: 'USD',
      payment_terms: '',
      delivery_terms: '',
      warranty_terms: '',
      start_date: '',
      end_date: '',
      delivery_date: '',
      notes: ''
    });
    setCreateItemsFromBudget(true);
    setBudgetIncreasePercentage(0);
    
    // Clear search terms
    setProjectSearchTerm('');
    setCustomerSearchTerm('');
    setEditingContract(null);
    setShowCreateForm(false); // Hide the create form
    setShowItemsForm(false);
    setEditingItem(null);
    setSuccessMessage(''); // Clear success message
  };

  const resetItemForm = () => {
    setItemFormData({
      item_number: '',
      description: '',
      quantity: 1,
      unit_of_measure: 'EA',
      unit_price: '',
      total_amount: '',
      markup_percentage: budgetIncreasePercentage,
      status: 'active',
      cost_code_vuid: null,
      cost_type_vuid: null,
      specifications: '',
      delivery_location: '',
      delivery_date: '',
      warranty_info: '',
      notes: '',
      contract_vuid: ''
    });
    setNewItemData({
      item_number: '',
      cost_code_vuid: '',
      cost_type_vuid: '',
      description: '',
      total_amount: '',
      markup_percentage: budgetIncreasePercentage,
      apply_fee_to_labor_schedule: false,
      notes: ''
    });
    setEditingItem(null);
    setShowItemsForm(false);
  };

  const handleShowCreateForm = () => {
    const searchParams = new URLSearchParams(location.search);
    setShowCreateForm(true);
    setEditingContract(null);
    // Reset form data but don't hide the form
    setFormData({
      project_vuid: searchParams.get('project') || '',
      contract_number: '',
      contract_date: '',
      customer_vuid: '',
      accounting_period_vuid: '',
      contract_type: CONTRACT_TYPES[0].value,
      status: 'active',
      description: '',
      total_amount: '',
      currency: 'USD',
      payment_terms: '',
      delivery_terms: '',
      warranty_terms: '',
      start_date: '',
      end_date: '',
      delivery_date: '',
      notes: ''
    });
    setSuccessMessage('');
    
    // Scroll to and focus on the contract form header when creating a new contract
    setTimeout(() => {
      if (contractFormHeaderRef.current) {
        contractFormHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Focus on the first input field (project selector)
        setTimeout(() => {
          const firstInput = contractFormHeaderRef.current?.querySelector('input[type="text"]');
          if (firstInput) {
            firstInput.focus();
          }
        }, 100);
      }
    }, 100);
  };

  const handleShowItemsForm = () => {
    setShowItemsForm(true);
    setEditingItem(null);
    resetItemForm();
  };

  const closeItemsForm = () => {
    setShowItemsForm(false);
    setSelectedContract(null);
    setContractItems([]);
    setEditingItem(null);
    resetItemForm();
  };

  const closeForm = () => {
    setShowCreateForm(false);
    setEditingContract(null);
    resetForm();
  };

  // Refresh contract amounts for all contracts
  const refreshContractAmounts = async () => {
    try {
      for (const contract of contracts) {
        if (contract.vuid) {
          try {
            const itemsResponse = await axios.get(`${baseURL}/api/project-contracts/${contract.vuid}/items`);
            const items = itemsResponse.data || [];
            
            if (items.length > 0) {
              const total = items.reduce((sum, item) => sum + (parseFloat(item.total_amount || 0) || 0), 0);
              
              // Update local contract state
              setContracts(prev => prev.map(c => 
                c.vuid === contract.vuid ? { ...c, contract_amount: total } : c
              ));
              
              // Update backend (non-blocking)
              axios.put(`${baseURL}/api/project-contracts/${contract.vuid}`, {
                contract_amount: total
              }).catch(err => {
                console.warn(`Failed to update backend for contract ${contract.contract_number}:`, err);
              });
            }
          } catch (err) {
            console.warn(`Error refreshing amount for contract ${contract.contract_number}:`, err);
          }
        }
      }
    } catch (err) {
      console.warn('Error refreshing contract amounts:', err);
    }
  };

  // Handle going back to main contract view after adding items
  const handleDoneAddingItems = () => {
    setShowItemsForm(false);
    setSelectedContract(null);
    setShowCreateForm(false);
    setEditingItem(null);
    setEditingContract(null);
    setSuccessMessage(''); // Clear success message
    // Reset the item form data
    setItemFormData({
      item_number: '',
      description: '',
      quantity: 1,
      unit_of_measure: 'EA',
      unit_price: '',
      total_amount: '',
      status: 'active',
      cost_code_vuid: null,
      cost_type_vuid: null,
      specifications: '',
      delivery_location: '',
      delivery_date: '',
      warranty_info: '',
      notes: '',
      contract_vuid: ''
    });
  };

  // Handle edit contract
  const handleEditContract = (contract) => {
    setEditingContract(contract);
    setFormData({
      project_vuid: contract.project_vuid || '',
      contract_number: contract.contract_number || '',
      contract_date: contract.contract_date || '',
              customer_vuid: contract.customer_vuid || '',
        accounting_period_vuid: contract.accounting_period_vuid || '',
      contract_type: contract.contract_type || CONTRACT_TYPES[0].value,
      status: contract.status || 'active',
      description: contract.description || '',
      total_amount: contract.total_amount || '',
      currency: contract.currency || 'USD',
      payment_terms: contract.payment_terms || '',
      delivery_terms: contract.delivery_terms || '',
      warranty_terms: contract.warranty_terms || '',
      start_date: contract.start_date || '',
      end_date: contract.end_date || '',
      delivery_date: contract.delivery_date || '',
      notes: contract.notes || ''
    });
    setShowCreateForm(true);
  };

  // Handle delete contract
  const handleDeleteContract = async (contract) => {
    if (window.confirm('Are you sure you want to delete this contract? This will also delete all associated contract items and allocations.')) {
      try {
        await axios.delete(`${baseURL}/api/project-contracts/${contract.vuid}`);
        setContracts(prev => prev.filter(c => c.vuid !== contract.vuid));
        
        // If we were viewing items for this contract, clear the view
        if (selectedContract && selectedContract.vuid === contract.vuid) {
          setSelectedContract(null);
          setShowItemsForm(false);
          setContractItems([]);
          setAllocationsByItem({});
        }
        
        setSuccessMessage('Contract and all associated items deleted successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } catch (err) {
        console.error('Error deleting contract:', err);
        setError('Error deleting contract: ' + err.message);
      }
    }
  };

  // Fetch integrations
  const fetchIntegrations = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/integrations`);
      
      // Filter integrations to only show those that have 'project_contracts' enabled or are Procore
      const filteredIntegrations = response.data.filter(integration => 
        integration.status === 'active' && 
        (integration.integration_type === 'procore' || 
         (integration.enabled_objects && integration.enabled_objects.project_contracts === true))
      );
      
      setIntegrations(filteredIntegrations);
    } catch (error) {
      console.error('Error fetching integrations:', error);
    }
  };

  // Handle integration modal
  const handleIntegrationModal = (contract) => {
    setSelectedContractForIntegration(contract);
    setSelectedIntegration(null);
    setShowIntegrationModal(true);
    fetchIntegrations();
  };

  // Handle send to integration
  const handleSendToIntegration = async () => {
    if (!selectedIntegration) {
      alert('Please select an integration');
      return;
    }

    if (!selectedContractForIntegration) {
      alert('No contract selected');
      return;
    }

    setSendingToIntegration(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/integrations/${selectedIntegration.vuid}/send-contract`,
        { contract_vuid: selectedContractForIntegration.vuid },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        const message = response.data.message || 
          `Contract "${selectedContractForIntegration.contract_number}" sent to ${selectedIntegration.integration_name} successfully!`;
        
        let fullMessage = message;
        if (response.data.line_items_created !== undefined) {
          fullMessage += `\n\nLine Items: ${response.data.line_items_created} created`;
          if (response.data.line_items_updated > 0) {
            fullMessage += `, ${response.data.line_items_updated} updated`;
          }
          if (response.data.line_items_failed > 0) {
            fullMessage += `, ${response.data.line_items_failed} failed`;
          }
        }
        
        if (response.data.errors && response.data.errors.length > 0) {
          fullMessage += `\n\nErrors:\n${response.data.errors.join('\n')}`;
        }
        
        alert(fullMessage);
        
        // Close the modal
        setShowIntegrationModal(false);
        setSelectedContractForIntegration(null);
        setSelectedIntegration(null);
      } else {
        alert(`Error: ${response.data.error || 'Failed to send contract'}`);
      }
    } catch (error) {
      console.error('Error sending contract to integration:', error);
      const data = error.response?.data || {};
      const errorMsg = data.error || data.message || error.message || 'Failed to send contract to integration';
      const lineErrors = Array.isArray(data.errors) ? data.errors.slice(0, 8).join('\n') : '';
      alert(lineErrors ? `Error: ${errorMsg}\n\n${lineErrors}` : `Error: ${errorMsg}`);
    } finally {
      setSendingToIntegration(false);
    }
  };

  // Handle retrieve from integration
  const handleRetrieveFromIntegration = async (integration) => {
    try {
      // Here you would implement the logic to retrieve contracts from the selected integration
            
      // For now, just show a success message
      alert(`Contracts retrieved from ${integration.integration_name} successfully!`);
      
      // Close the modal
      setShowRetrieveModal(false);
    } catch (error) {
      console.error('Error retrieving contracts from integration:', error);
      alert('Error retrieving contracts from integration');
    }
  };

  // Handle retrieve modal
  const handleRetrieveModal = () => {
    setShowRetrieveModal(true);
  };


  // Pagination
  const startIndex = (currentPage - 1) * contractsPerPage;
  const paginatedContracts = filteredContracts.slice(startIndex, startIndex + contractsPerPage);

  // Excel-style form functions for contract items
  // Handle cost code/type changes for existing items
  const handleCostCodeTypeChange = async (itemVuid, newCostCodeVuid, newCostTypeVuid) => {
            
    const item = contractItems.find(i => i.vuid === itemVuid);
    if (!item) {
            return;
    }
    
    // If both cost code and type are provided, create new allocation
    if (newCostCodeVuid && newCostTypeVuid) {
            
      // Validate the new combination
      const validation = validateCostCodeTypeCombination(newCostCodeVuid, newCostTypeVuid, itemVuid);
      if (!validation.isValid) {
        alert(`Cannot change to this combination: ${validation.message}`);
        // Revert the change in the form
        return false; // Signal that the change should be reverted
      }
      
      // Create new allocation
      const newAllocation = {
        vuid: `temp-allocation-${Date.now()}`,
        contract_item_vuid: itemVuid,
        cost_code_vuid: newCostCodeVuid,
        cost_type_vuid: newCostTypeVuid,
        notes: 'Auto-generated allocation'
      };
      
      // Update allocationsByItem
      setAllocationsByItem(prev => ({
        ...prev,
        [itemVuid]: [newAllocation]
      }));
      
            return true; // Signal that the change was successful
    } else {
      // If either is missing, clear allocations
            setAllocationsByItem(prev => {
        const updated = { ...prev };
        delete updated[itemVuid];
        return updated;
      });
      return true; // Signal that the change was successful
    }
  };

  const findContractCostCode = (vuid) => {
    if (!vuid) return null;
    return getAllCostCodes().find((code) => code.vuid === vuid)
      || projectBudgetLines.find((line) => line.cost_code_vuid === vuid)?.cost_code
      || null;
  };

  const costCodeMenuOptions = () => contractCostCodeOptions().map((costCode) => ({
    vuid: costCode.vuid,
    primary: costCode.code || '',
    secondary: costCode.description || '',
  }));

  const costTypeMenuOptions = (costCodeVuid, selectedTypeVuid) => (
    contractCostTypeOptions(costCodeVuid, selectedTypeVuid).map((costType) => ({
      vuid: costType.vuid,
      primary: `${costType.cost_type} (${costType.abbreviation})`,
      secondary: costType.description || '',
    }))
  );

  const costTypeDisplay = (vuid) => {
    const costType = costTypes.find((type) => type.vuid === vuid);
    return costType ? `${costType.cost_type} (${costType.abbreviation})` : '';
  };

  const isFeeCostType = (costType) => {
    if (!costType) return false;
    const name = String(costType.cost_type || '').trim().toLowerCase();
    const abbreviation = String(costType.abbreviation || '').trim().toLowerCase();
    return abbreviation === 'f' || name === 'fee' || name === 'fees' || name.startsWith('fee ');
  };

  const isLaborCostTypeVuid = (vuid) => {
    const costType = costTypes.find((type) => type.vuid === vuid);
    if (!costType) return false;
    const name = String(costType.cost_type || '').trim().toLowerCase();
    const abbreviation = String(costType.abbreviation || '').trim().toLowerCase();
    return abbreviation === 'l' || abbreviation === 'lab' || name === 'labor';
  };

  const showLaborScheduleFee = (costCodeVuid, costTypeVuid) => (
    laborScheduleCostCodes.has(costCodeVuid) && isLaborCostTypeVuid(costTypeVuid)
  );

  const costTypeSortKey = (vuid) => {
    const costType = costTypes.find((type) => type.vuid === vuid);
    if (!costType) return '2\t';
    const name = String(costType.cost_type || '').toLowerCase();
    const abbreviation = String(costType.abbreviation || '').toLowerCase();
    return `${isFeeCostType(costType) ? '0' : '1'}\t${abbreviation}\t${name}`;
  };

  const contractItemSortValue = (item, key) => {
    if (key === 'item_number') {
      const itemNumber = parseInt(item.item_number || '0', 10);
      return Number.isNaN(itemNumber) ? 0 : itemNumber;
    }
    if (key === 'cost_code') return String(findContractCostCode(item.cost_code_vuid)?.code || item.cost_code?.code || '').toLowerCase();
    if (key === 'cost_type') return costTypeSortKey(item.cost_type_vuid);
    if (key === 'markup') return parseFloat(item.markup_percentage) || 0;
    if (key === 'labor_fee') return item.apply_fee_to_labor_schedule ? 1 : 0;
    if (key === 'description') return String(item.description || '').toLowerCase();
    if (key === 'total_amount') return parseFloat(item.total_amount) || 0;
    if (key === 'change_orders') return getChangeOrderAmount(item);
    if (key === 'new_amount') return (parseFloat(item.total_amount) || 0) + getChangeOrderAmount(item);
    if (key === 'notes') return String(item.notes || '').toLowerCase();
    return '';
  };

  const toggleContractItemSort = (key) => {
    setContractItemSort((current) => (
      current.key === key
        ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    ));
  };

  const contractItemSortMark = (key) => (
    contractItemSort.key === key ? (contractItemSort.direction === 'asc' ? ' ▲' : ' ▼') : ''
  );

  const sortedContractItems = [...(contractItems || [])].sort((a, b) => {
    const direction = contractItemSort.direction === 'desc' ? -1 : 1;
    const numeric = ['item_number', 'markup', 'labor_fee', 'total_amount', 'change_orders', 'new_amount'].includes(contractItemSort.key);
    let compared = 0;
    if (contractItemSort.key === 'cost_code' || contractItemSort.key === 'cost_type') {
      const primary = contractItemSort.key;
      const secondary = primary === 'cost_code' ? 'cost_type' : 'cost_code';
      compared = String(contractItemSortValue(a, primary)).localeCompare(String(contractItemSortValue(b, primary)));
      if (compared === 0) {
        compared = String(contractItemSortValue(a, secondary)).localeCompare(String(contractItemSortValue(b, secondary)));
      }
    } else {
      const left = contractItemSortValue(a, contractItemSort.key);
      const right = contractItemSortValue(b, contractItemSort.key);
      compared = numeric ? left - right : String(left).localeCompare(String(right));
    }
    if (compared !== 0) return compared * direction;
    const itemNumberCompare = String(a.item_number || '').localeCompare(String(b.item_number || ''), undefined, { numeric: true });
    if (itemNumberCompare !== 0) return itemNumberCompare;
    return String(a.vuid || '').localeCompare(String(b.vuid || ''));
  });

  const selectContractItemCostCode = async (item, index, newCostCodeVuid) => {
    const oldMarkup = usesLineMarkups(feeLevel)
      ? getMarkupFromFeeSettings(item.cost_code_vuid, item.cost_type_vuid) ?? (item.markup_percentage ?? 0)
      : (item.markup_percentage ?? 0);
    const newItems = [...contractItems];
    let nextType = '';
    newItems[index] = {
      ...newItems[index],
      cost_code_vuid: newCostCodeVuid,
      apply_fee_to_labor_schedule: false,
    };
    if (newCostCodeVuid) {
      const availableCostTypes = contractCostTypeOptions(newCostCodeVuid, '');
      if (availableCostTypes.length === 1) {
        nextType = availableCostTypes[0].vuid;
        newItems[index] = { ...newItems[index], cost_type_vuid: nextType };
        if (usesLineMarkups(feeLevel)) {
          const newMarkup = getMarkupFromFeeSettings(newCostCodeVuid, nextType) ?? 0;
          const currentTotal = parseFloat(newItems[index].total_amount || 0);
          const total = recalculateTotalAmountWithNewMarkup(currentTotal, oldMarkup, newMarkup);
          const quantity = parseFloat(newItems[index].quantity || 0);
          newItems[index] = {
            ...newItems[index],
            markup_percentage: newMarkup,
            total_amount: total,
            unit_price: quantity ? Math.round((total / quantity) * 100) / 100 : total,
          };
        }
      } else {
        newItems[index] = { ...newItems[index], cost_type_vuid: '' };
      }
    } else {
      newItems[index] = { ...newItems[index], cost_type_vuid: '' };
    }
    setContractItems(newItems);
    setCostCodeDropdownOpen(null);
    await handleCostCodeTypeChange(item.vuid, newCostCodeVuid, nextType);
  };

  const selectContractItemCostType = async (item, index, newCostTypeVuid) => {
    const oldMarkup = usesLineMarkups(feeLevel)
      ? getMarkupFromFeeSettings(item.cost_code_vuid, item.cost_type_vuid) ?? (item.markup_percentage ?? 0)
      : (item.markup_percentage ?? 0);
    const newItems = [...contractItems];
    newItems[index] = { ...newItems[index], cost_type_vuid: newCostTypeVuid };
    if (usesLineMarkups(feeLevel) && item.cost_code_vuid && newCostTypeVuid) {
      const newMarkup = getMarkupFromFeeSettings(item.cost_code_vuid, newCostTypeVuid) ?? 0;
      const currentTotal = parseFloat(newItems[index].total_amount || 0);
      const total = recalculateTotalAmountWithNewMarkup(currentTotal, oldMarkup, newMarkup);
      const quantity = parseFloat(newItems[index].quantity || 0);
      newItems[index] = {
        ...newItems[index],
        markup_percentage: newMarkup,
        total_amount: total,
        unit_price: quantity ? Math.round((total / quantity) * 100) / 100 : total,
      };
    }
    setContractItems(newItems);
    setCostTypeDropdownOpen(null);
    await handleCostCodeTypeChange(item.vuid, item.cost_code_vuid, newCostTypeVuid);
  };

  const scrollToNewContractItem = () => {
    window.setTimeout(() => {
      const row = document.getElementById('new-contract-item-row');
      if (!row) return;
      row.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }, 50);
  };

  const scrollToContractItem = (vuid) => {
    window.setTimeout(() => {
      const row = document.getElementById(`contract-item-${vuid}`);
      if (!row) return;
      row.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    }, 50);
  };

  const persistNewContractItem = async (item) => {
    if (item.cost_code_vuid && item.cost_type_vuid) {
      const validation = validateCostCodeTypeCombination(item.cost_code_vuid, item.cost_type_vuid, item.vuid);
      if (!validation.isValid) {
        throw new Error(`Item ${item.item_number}: ${validation.message}`);
      }
    }
    const res = await axios.post(`${baseURL}/api/project-contract-items`, {
      contract_vuid: item.contract_vuid,
      item_number: item.item_number,
      cost_code_vuid: item.cost_code_vuid || null,
      cost_type_vuid: item.cost_type_vuid || null,
      description: item.description,
      total_amount: item.total_amount || 0,
      notes: item.notes || '',
      status: item.status || 'active',
      markup_percentage: parseFloat(item.markup_percentage) || 0,
      apply_fee_to_labor_schedule: Boolean(item.apply_fee_to_labor_schedule)
    });
    const savedItem = {
      ...res.data,
      cost_code_vuid: item.cost_code_vuid || res.data.cost_code_vuid,
      cost_type_vuid: item.cost_type_vuid || res.data.cost_type_vuid
    };
    if (item.cost_code_vuid && item.cost_type_vuid) {
      try {
        await axios.post(`${baseURL}/api/project-contract-items/${savedItem.vuid}/allocations`, {
          cost_code_vuid: item.cost_code_vuid,
          cost_type_vuid: item.cost_type_vuid,
          notes: 'Auto-generated allocation'
        });
        const allocsRes = await axios.get(`${baseURL}/api/project-contract-items/${savedItem.vuid}/allocations`);
        const savedAllocs = allocsRes.data || [];
        savedItem.allocations = savedAllocs;
        setAllocationsByItem((prev) => ({ ...prev, [savedItem.vuid]: savedAllocs }));
      } catch (allocErr) {
        console.error('Error creating allocation for item', savedItem.item_number, allocErr);
      }
    }
    return savedItem;
  };

  const draftHasContent = () => Boolean(
    String(newItemData.item_number || '').trim()
    || newItemData.cost_code_vuid
    || String(newItemData.description || '').trim()
    || String(newItemData.total_amount || '').trim()
  );

  const clearNewItemDraft = () => {
    setNewItemData({
      item_number: '',
      cost_code_vuid: '',
      cost_type_vuid: '',
      description: '',
      total_amount: '',
      markup_percentage: budgetIncreasePercentage,
      apply_fee_to_labor_schedule: false,
      notes: ''
    });
  };

  const contractItemDiffersFromSaved = (item) => {
    const saved = savedContractItemsRef.current.get(item.vuid);
    if (!saved) return true;
    const textFields = [
      'item_number', 'description', 'unit_of_measure', 'status', 'cost_code_vuid',
      'cost_type_vuid', 'notes', 'specifications', 'delivery_location', 'delivery_date', 'warranty_info'
    ];
    if (textFields.some((field) => String(item[field] ?? '') !== String(saved[field] ?? ''))) return true;
    const numberFields = ['quantity', 'unit_price', 'total_amount', 'markup_percentage'];
    if (numberFields.some((field) => Math.abs((parseFloat(item[field]) || 0) - (parseFloat(saved[field]) || 0)) > 0.001)) {
      return true;
    }
    return Boolean(item.apply_fee_to_labor_schedule) !== Boolean(saved.apply_fee_to_labor_schedule);
  };

  const commitDraftLine = async () => {
    if (!draftHasContent()) return null;
    if (newItemData.cost_code_vuid && newItemData.cost_type_vuid) {
      const validation = validateCostCodeTypeCombination(newItemData.cost_code_vuid, newItemData.cost_type_vuid);
      if (!validation.isValid) {
        throw new Error(validation.message);
      }
    }
    const newItem = {
      ...newItemData,
      item_number: String(newItemData.item_number || '').trim() || String(contractItems.length + 1).padStart(4, '0'),
      description: String(newItemData.description || '').trim()
        || findContractCostCode(newItemData.cost_code_vuid)?.description
        || 'New item',
      total_amount: String(newItemData.total_amount || '').trim() === '' ? 0 : newItemData.total_amount,
      contract_vuid: selectedContract.vuid,
      status: 'active',
      markup_percentage: parseFloat(newItemData.markup_percentage) || 0,
      apply_fee_to_labor_schedule: Boolean(newItemData.apply_fee_to_labor_schedule),
      vuid: `temp-${Date.now()}`
    };
    const savedItem = await persistNewContractItem(newItem);
    savedContractItemsRef.current.set(savedItem.vuid, savedItem);
    setContractItems((prev) => [...prev, savedItem]);
    clearNewItemDraft();
    return savedItem;
  };

  const handleAddNewItem = async () => {
    if (!draftHasContent()) {
      scrollToNewContractItem();
      return;
    }
    if (savingNewItemRef.current) return;
    savingNewItemRef.current = true;
    setError('');
    try {
      const savedItem = await commitDraftLine();
      if (!savedItem) return;
      setSuccessMessage(`Item ${savedItem.item_number} saved.`);
      setTimeout(() => setSuccessMessage(''), 3000);
      scrollToContractItem(savedItem.vuid);
    } catch (err) {
      setError(`The new line was not saved: ${err.response?.data?.error || err.message}`);
    } finally {
      savingNewItemRef.current = false;
    }
  };

  const handleSaveAllItems = async () => {
    const failures = [];
    const savedByTempVuid = new Map();
    let draftedItem = null;
    try {
      if (draftHasContent()) {
        try {
          draftedItem = await commitDraftLine();
        } catch (draftErr) {
          const message = draftErr.response?.data?.error || draftErr.message;
          setError(`The new line was not saved: ${message}`);
          return false;
        }
      }
      for (const item of contractItems) {
        try {
          if (String(item.vuid || '').startsWith('temp-')) {
            const savedItem = await persistNewContractItem(item);
            savedContractItemsRef.current.set(savedItem.vuid, savedItem);
            savedByTempVuid.set(item.vuid, savedItem);
            setContractItems(prev => prev.map(existingItem =>
              existingItem.vuid === item.vuid ? savedItem : existingItem
            ));
          } else if (contractItemDiffersFromSaved(item)) {
            // Update an existing item that changed
                                                const updateResponse = await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
              item_number: item.item_number,
              description: item.description,
              quantity: item.quantity,
              unit_of_measure: item.unit_of_measure,
              unit_price: item.unit_price,
              total_amount: item.total_amount,
              status: item.status,
              cost_code_vuid: item.cost_code_vuid,
              cost_type_vuid: item.cost_type_vuid,
              specifications: item.specifications,
              delivery_location: item.delivery_location,
              delivery_date: item.delivery_date,
              warranty_info: item.warranty_info,
              notes: item.notes,
              markup_percentage: parseFloat(item.markup_percentage) || 0,
              apply_fee_to_labor_schedule: Boolean(item.apply_fee_to_labor_schedule)
            });
                                    
            // Update the local state to reflect the saved changes
            const mergedItem = { ...item, ...updateResponse.data };
            savedContractItemsRef.current.set(item.vuid, mergedItem);
            setContractItems(prev => prev.map(existingItem => 
              existingItem.vuid === item.vuid ? { ...existingItem, ...updateResponse.data } : existingItem
            ));
                        
            // Check if it needs allocations created
            if (item.cost_code_vuid && item.cost_type_vuid) {
              const existingAllocs = allocationsByItem[item.vuid] || [];
              if (existingAllocs.length === 0) {
                // Validate cost code cost type combination uniqueness before creating allocation
                const validation = validateCostCodeTypeCombination(item.cost_code_vuid, item.cost_type_vuid, item.vuid);
                if (!validation.isValid) {
                  throw new Error(`Item ${item.item_number}: ${validation.message}`);
                }
                
                // No allocations exist but cost code/type are set - create allocation
                                await axios.post(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`, {
                  cost_code_vuid: item.cost_code_vuid,
                  cost_type_vuid: item.cost_type_vuid,
                  notes: 'Auto-generated allocation'
                });
                                
                // Refresh allocations for this item
                const allocsRes = await axios.get(`${baseURL}/api/project-contract-items/${item.vuid}/allocations`);
                const savedAllocs = allocsRes.data || [];
                setAllocationsByItem(prev => ({ ...prev, [item.vuid]: savedAllocs }));
              }
            }
          }
        } catch (perItemErr) {
          console.error('Error processing item:', item.item_number, 'vuid:', item.vuid, perItemErr);
          failures.push(`Item ${item.item_number || ''}: ${perItemErr.response?.data?.error || perItemErr.message}`);
        }
      }
      
      // Update contract total amount
      if (selectedContract?.vuid) {
        const updatedItems = contractItems.map((item) => savedByTempVuid.get(item.vuid) || item);
        if (draftedItem && !updatedItems.some((item) => item.vuid === draftedItem.vuid)) {
          updatedItems.push(draftedItem);
        }
        // Round to 2 decimal places to avoid floating point precision issues
        const total = Math.round(updatedItems.reduce((sum, item) => sum + (parseFloat(item.total_amount) || 0), 0) * 100) / 100;
        
        try {
          await axios.put(`${baseURL}/api/project-contracts/${selectedContract.vuid}`, {
            contract_amount: total
          });
          // Update local contract state
          setContracts(prev => prev.map(c => 
            c.vuid === selectedContract.vuid ? { ...c, contract_amount: total } : c
          ));
          setSelectedContract(prev => prev ? { ...prev, contract_amount: total } : null);
        } catch (err) {
          console.error('Error updating contract total:', err);
        }
      }
      
      if (failures.length) {
        setError(failures.join(' '));
        return false;
      }
      setError('');
      setSuccessMessage('All contract items saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      return true;
    } catch (err) {
      setError('Error saving contract items: ' + err.message);
      return false;
    }
  };

  // Handle Done button - close lines view and refresh contracts
  const handleDoneEditing = async () => {
    const hasUnsavedWork = draftHasContent() || contractItems.some((item) => (
      String(item.vuid || '').startsWith('temp-') || contractItemDiffersFromSaved(item)
    ));
    if (hasUnsavedWork) {
      const saved = await handleSaveAllItems();
      if (!saved) return;
    }
    try {
      setShowItemsForm(false);
      await fetchData();
      setSuccessMessage('Contract lines saved and closed.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error refreshing contracts:', err);
      setError('Error refreshing contracts: ' + err.message);
    }
  };

  // Option: auto-create contract items from budget lines when creating a contract
  const [createItemsFromBudget, setCreateItemsFromBudget] = useState(true);
  const [budgetIncreasePercentage, setBudgetIncreasePercentage] = useState(0);
  
  // Post-creation percentage adjustment
  const [adjustmentPercentage, setAdjustmentPercentage] = useState(0);
  const [showPercentageAdjustment, setShowPercentageAdjustment] = useState(false);
  const [originalAmounts, setOriginalAmounts] = useState({}); // Store original amounts before adjustments

  // Function to apply percentage adjustment to all contract items
  const applyPercentageAdjustment = async () => {
    if (!selectedContract || !contractItems.length) return;
    
    try {
      setError('');
      
      // Store original amounts if not already stored
      if (Object.keys(originalAmounts).length === 0) {
        const originals = {};
        contractItems.forEach(item => {
          originals[item.vuid] = item.total_amount;
        });
        setOriginalAmounts(originals);
      }
      
      // Calculate new amounts and update each item
      const updatePromises = contractItems.map(async (item) => {
        const originalAmount = originalAmounts[item.vuid] || item.total_amount;
        const adjustmentMultiplier = 1 (adjustmentPercentage / 100);
        const newAmount = originalAmount * adjustmentMultiplier;
        
        // Update the item via API
        const response = await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
          total_amount: newAmount,
          unit_price: newAmount // Assuming quantity is + 1 for budget-based items
        });
        
        return response.data;
      });
      
      await Promise.all(updatePromises);
      
      // Refresh contract items to show updated amounts
      await fetchContractItems(selectedContract.vuid);
      
      setSuccessMessage(`Successfully applied ${adjustmentPercentage}% adjustment to all contract items`);
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (error) {
      console.error('Error applying percentage adjustment:', error);
      setError('Error applying percentage adjustment: ' + error.message);
    }
  };
  
  // Function to reset amounts to original values
  const resetToOriginalAmounts = async () => {
    if (!selectedContract || !contractItems.length || Object.keys(originalAmounts).length === 0) return;
    
    try {
      setError('');
      
      // Reset each item to its original amount
      const updatePromises = contractItems.map(async (item) => {
        const originalAmount = originalAmounts[item.vuid];
        if (originalAmount !== undefined) {
          const response = await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
            total_amount: originalAmount,
            unit_price: originalAmount
          });
          return response.data;
        }
      });
      
      await Promise.all(updatePromises);
      
      // Refresh contract items and reset states
      await fetchContractItems(selectedContract.vuid);
      setAdjustmentPercentage(0);
      setOriginalAmounts({});
      
      setSuccessMessage('Successfully reset all amounts to original values');
      setTimeout(() => setSuccessMessage(''), 5000);
      
    } catch (error) {
      console.error('Error resetting to original amounts:', error);
      setError('Error resetting amounts: ' + error.message);
    }
  };

  const budgetLineHasAmount = (line) => {
    const amount = parseFloat(line?.budget_amount || 0);
    return Number.isFinite(amount) && amount !== 0;
  };

  // Helper: create contract items for each budget line in the selected project
  const generateItemsFromBudget = async (newContract) => {
    try {
      const projectVuid = formData.project_vuid || newContract.project_vuid;
      if (!projectVuid) return;

      const [linesRes, projectCostCodesRes] = await Promise.all([
        axios.get(`${baseURL}/api/projects/${projectVuid}/budget-lines`),
        axios.get(`${baseURL}/api/projects/${projectVuid}/cost-codes`).catch((e) => {
          console.warn('Failed to fetch project cost codes:', e);
          return { data: [] };
        }),
      ]);
      const allLines = (Array.isArray(linesRes.data) ? linesRes.data : []).filter(budgetLineHasAmount);
      const fetchedProjectCostCodes = projectCostCodesRes.data || [];
      setProjectCostCodes(fetchedProjectCostCodes);

      const totalLines = allLines.length;
      setBudgetLinesProgress({ current: 0, total: totalLines });
      if (!totalLines) return;

      const items = allLines.map((line, index) => {
        const cost_code_vuid = line.cost_code_vuid || null;
        const cost_type_vuid = line.cost_type_vuid || null;
        let costCode = line.cost_code || costCodes.find(c => c.vuid === cost_code_vuid);
        if (!costCode) {
          costCode = fetchedProjectCostCodes.find(c => c.vuid === cost_code_vuid);
        }
        const costCodeLabel = costCode
          ? [costCode.code, costCode.description].filter(Boolean).join(' — ')
          : '';
        const budgetAmount = parseFloat(line.budget_amount || 0);
        const increaseMultiplier = 1 + (budgetIncreasePercentage / 100);
        const total_amount = Math.round((budgetAmount * increaseMultiplier) * 100) / 100;
        return {
          item_number: String(index + 1).padStart(4, '0'),
          cost_code_vuid,
          cost_type_vuid,
          description: costCodeLabel || line.notes || line.description || 'Budget Line',
          quantity: 1,
          unit_price: total_amount,
          total_amount,
          markup_percentage: budgetIncreasePercentage,
          status: 'active',
          notes: '',
          allocations: cost_code_vuid && cost_type_vuid ? [{
            cost_code_vuid,
            cost_type_vuid,
          }] : [],
        };
      });

      const bulkRes = await axios.post(`${baseURL}/api/project-contract-items/bulk`, {
        contract_vuid: newContract.vuid,
        items,
      });
      const createdItems = bulkRes.data?.items || [];
      setBudgetLinesProgress({ current: createdItems.length, total: totalLines });

      if (bulkRes.data?.errors?.length) {
        setError(`${bulkRes.data.errors.length} budget lines could not be copied onto the contract.`);
      }

      if (createdItems.length > 0) {
        setAllocationsByItem(prev => {
          const next = { ...prev };
          createdItems.forEach(item => {
            if (Array.isArray(item.allocations) && item.allocations.length) {
              next[item.vuid] = item.allocations;
            }
          });
          return next;
        });

        setContractItems(prev => {
          const existingVuids = new Set(prev.map(item => item.vuid));
          const newItems = createdItems.filter(item => !existingVuids.has(item.vuid));
          return [...prev, ...newItems].sort((a, b) => {
            return (a.item_number || '').localeCompare(b.item_number || '');
          });
        });

        const total = Math.round((parseFloat(bulkRes.data.contract_amount) || 0) * 100) / 100;
        setContracts(prev => prev.map(c =>
          c.vuid === newContract.vuid ? { ...c, contract_amount: total } : c
        ));
        setSelectedContract(prev => {
          if (prev && prev.vuid === newContract.vuid) {
            return { ...prev, contract_amount: total };
          }
          return prev;
        });
      }
    } catch (err) {
      console.error('Error generating items from budget:', err);
      const message = err.response?.data?.error || err.message;
      setError('Error generating items from budget: ' + message);
      setBudgetLinesProgress({ current: 0, total: 0 });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-600">Error: {error}</div>;
  }

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="project-contracts-page-header flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">Project Contracts</h1>
            <p className="mt-2 text-gray-600">Manage project contracts and sales orders</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleShowCreateForm}
              className="btn-claret"
              data-tooltip="Add Contract"
            >
              <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Add Contract</span>
            </button>
            <button
              onClick={() => {
                const searchParams = new URLSearchParams(location.search);
                const projectVuid = searchParams.get('project');
                const previewMatch = location.pathname.match(/^\/procore\/projects\/([^/]+)/);
                const url = projectVuid ? `/external-change-orders?project=${projectVuid}` : '/external-change-orders';
                navigate(previewMatch ? `/procore/projects/${previewMatch[1]}/change-orders` : url);
              }}
              className="btn-claret"
              data-tooltip="External Change Orders"
            >
              <svg className="w-5 h-5 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>External Change Orders</span>
            </button>
          </div>
        </div>

        {/* Unallocated Budget Lines Alert */}
        {showAllocationAlert && allocationStatus.has_unallocated_budget_lines && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-md">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75-1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-amber-800">
                  Unallocated Budget Lines
                </h3>
                <div className="mt-2 text-sm text-amber-700">
                  <p>
                    {allocationStatus.budget_lines_without_allocations} of {allocationStatus.total_budget_lines} budget lines 
                    are not allocated to any contract items. This may indicate missing contract coverage or budget planning issues.
                  </p>
                  {allocationStatus.unallocated_budget_lines && allocationStatus.unallocated_budget_lines.length > 0 && (
                    <div className="mt-3">
                      <p className="font-medium">Budget lines needing contract allocations:</p>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        {allocationStatus.unallocated_budget_lines.slice(0, 5).map((budgetLine, index) => (
                          <li key={index} className="text-xs">
                            {budgetLine.cost_code?.code || 'Unknown'} - {budgetLine.cost_type?.abbreviation || 'Unknown'}: {budgetLine.notes || 'No description'} 
                            ({formatCurrency(budgetLine.budget_amount)})
                          </li>
                        ))}
                        {allocationStatus.unallocated_budget_lines.length > 5 && (
                          <li className="text-xs text-amber-600">
                            ... and {allocationStatus.unallocated_budget_lines.length - 5} more budget lines
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Contract Form */}
        {showCreateForm && (
          <div ref={contractFormHeaderRef} className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">
              {editingContract ? 'Edit Contract' : 'Add New Contract'}
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-600 font-medium">{errors.error}</p>
                </div>
              )}
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="project_vuid" className="block text-xs font-semibold text-gray-900 mb-0.5">
                    Project *
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      placeholder={formData.project_vuid ? "Type to search projects..." : "Select project..."}
                      value={formData.project_vuid ? 
                        (projects.find(p => p.vuid === formData.project_vuid)?.project_name || '') : 
                        projectSearchTerm
                      }
                      onChange={(e) => {
                        if (!formData.project_vuid) {
                          setProjectSearchTerm(e.target.value);
                          setProjectDropdownOpen(true);
                        }
                      }}
                      onClick={() => {
                        if (!formData.project_vuid) {
                          setProjectDropdownOpen(!projectDropdownOpen);
                        } else {
                          // Clear selection to allow searching
                          setFormData(prev => ({ ...prev, project_vuid: '' }));
                          setProjectSearchTerm('');
                          setProjectDropdownOpen(true);
                        }
                      }}
                      className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition-all ${
                        errors.project_vuid ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {projectDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 border-b border-gray-200">
                          <input
                            type="text"
                            placeholder="Search projects..."
                            value={projectSearchTerm}
                            onChange={(e) => setProjectSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                        </div>
                        {getFilteredProjects().length > 0 ? (
                          getFilteredProjects().map(project => (
                            <div
                              key={project.vuid}
                              onClick={async () => {
                                setFormData(prev => ({ ...prev, project_vuid: project.vuid }));
                                setProjectDropdownOpen(false);
                                setProjectSearchTerm('');
                              
                              // Fetch project-specific cost codes for this project
                              try {
                                const projectCostCodesRes = await axios.get(`${baseURL}/api/projects/${project.vuid}/cost-codes`);
                                setProjectCostCodes(projectCostCodesRes.data || []);
                                                              } catch (e) {
                                console.warn('Failed to fetch project cost codes:', e);
                                setProjectCostCodes([]);
                              }
                              fetchProjectSettings(project.vuid);
                              
                              // Fetch project budget lines for this project
                              try {
                                const budgetLinesRes = await axios.get(`${baseURL}/api/projects/${project.vuid}/budget-lines`);
                                setProjectBudgetLines(budgetLinesRes.data || []);
                                                              } catch (e) {
                                console.warn('Failed to fetch project budget lines:', e);
                                setProjectBudgetLines([]);
                              }
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{project.project_number}</div>
                            <div className="text-sm text-gray-600">{project.project_name}</div>
                          </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-gray-500 text-center">
                            No projects found matching "{projectSearchTerm}"
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {errors.project_vuid && (
                    <p className="mt-1 text-sm text-red-600">{errors.project_vuid}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="contract_number" className="block text-xs font-semibold text-gray-900 mb-0.5">
                    Contract Number *
                  </label>
                  <input
                    type="text"
                    id="contract_number"
                    name="contract_number"
                    value={formData.contract_number}
                    onChange={handleInputChange}
                    className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition-all ${
                      errors.contract_number ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter contract number"
                    required
                  />
                  {errors.contract_number && (
                    <p className="mt-1 text-sm text-red-600">{errors.contract_number}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="customer_vuid" className="block text-xs font-semibold text-gray-900 mb-0.5">
                    Customer *
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      placeholder={formData.customer_vuid ? "Type to search customers..." : "Select customer..."}
                      value={formData.customer_vuid ? 
                        (customers.find(c => c.vuid === formData.customer_vuid)?.customer_name || '') : 
                        customerSearchTerm
                      }
                      onChange={(e) => {
                        if (!formData.customer_vuid) {
                          setCustomerSearchTerm(e.target.value);
                          setCustomerDropdownOpen(true);
                        }
                      }}
                      onClick={() => {
                        if (!formData.customer_vuid) {
                          setCustomerDropdownOpen(!customerDropdownOpen);
                        } else {
                          // Clear selection to allow searching
                          setFormData(prev => ({ ...prev, customer_vuid: '' }));
                          setCustomerSearchTerm('');
                          setCustomerDropdownOpen(true);
                        }
                      }}
                      className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition-all ${
                        errors.customer_vuid ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {customerDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 border-b border-gray-200">
                          <input
                            type="text"
                            placeholder="Search customers..."
                            value={customerSearchTerm}
                            onChange={(e) => setCustomerSearchTerm(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                        </div>
                        {getFilteredCustomers().length > 0 ? (
                          getFilteredCustomers().map(customer => (
                            <div
                              key={customer.vuid}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, customer_vuid: customer.vuid }));
                                setCustomerDropdownOpen(false);
                                setCustomerSearchTerm('');
                              }}
                              className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="font-medium text-gray-900">{customer.customer_name}</div>
                              <div className="text-sm text-gray-600">{customer.company_name}</div>
                            </div>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-gray-500 text-center">
                            No customers found matching "{customerSearchTerm}"
                          </div>
                        )}
                      </div>
                    )}
                    {errors.customer_vuid && (
                      <p className="mt-1 text-sm text-red-600">{errors.customer_vuid}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="accounting_period_vuid" className="block text-xs font-semibold text-gray-900 mb-0.5">
                    Accounting Period *
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      placeholder="Select accounting period..."
                      value={accountingPeriods.find(ap => ap.vuid === formData.accounting_period_vuid) ? 
                        `${accountingPeriods.find(ap => ap.vuid === formData.accounting_period_vuid)?.month}/${accountingPeriods.find(ap => ap.vuid === formData.accounting_period_vuid)?.year}` : ''}
                      onClick={() => setAccountingPeriodDropdownOpen(!accountingPeriodDropdownOpen)}
                      readOnly
                      className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition-all ${
                        errors.accounting_period_vuid ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {accountingPeriodDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {accountingPeriods.map(period => (
                          <div
                            key={period.vuid}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, accounting_period_vuid: period.vuid }));
                              setAccountingPeriodDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{period.month}/{period.year}</div>
                            <div className="text-sm text-gray-600">{period.status === 'open' ? 'Open' : 'Closed'}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {errors.accounting_period_vuid && (
                      <p className="mt-1 text-sm text-red-600">{errors.accounting_period_vuid}</p>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="contract_type" className="block text-xs font-semibold text-gray-900 mb-0.5">
                    Contract Type *
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      placeholder="Select contract type..."
                      value={
                        CONTRACT_TYPES.find(type => type.value === formData.contract_type)?.label || ''
                      }
                      onClick={() => setContractTypeDropdownOpen(!contractTypeDropdownOpen)}
                      readOnly
                      className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition-all ${
                        errors.contract_type ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {contractTypeDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                        <div className="py-1">
                          {CONTRACT_TYPES.map((type, index) => (
                            <div
                              key={type.value}
                              onClick={() => {
                                setFormData(prev => ({ ...prev, contract_type: type.value }));
                                setContractTypeDropdownOpen(false);
                              }}
                              className={`px-4 py-3 hover:bg-gray-100 cursor-pointer ${
                                index !== CONTRACT_TYPES.length - 1 ? 'border-b border-gray-100' : ''
                              }`}
                            >
                              {type.label}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {errors.contract_type && (
                    <p className="mt-1 text-sm text-red-600">{errors.contract_type}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="status" className="block text-xs font-semibold text-gray-900 mb-0.5">
                    Status
                  </label>
                  <div className="relative dropdown-container">
                    <input
                      type="text"
                      placeholder="Select status..."
                      value={formData.status === 'active' ? 'Active' : 
                             formData.status === 'completed' ? 'Completed' : 
                             formData.status === 'on-hold' ? 'On Hold' : 
                             formData.status === 'cancelled' ? 'Cancelled' : 
                             formData.status}
                      onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                      readOnly
                      className="w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition-all border-gray-300"
                    />
                    {statusDropdownOpen && (
                      <div className="absolute z-10 w-full mt-1 bg-white border-2 border-gray-300 rounded-lg shadow-lg">
                        <div className="py-1">
                          <div
                            onClick={() => {
                              setFormData(prev => ({ ...prev, status: 'active' }));
                              setStatusDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                          >
                            Active
                          </div>
                          <div
                            onClick={() => {
                              setFormData(prev => ({ ...prev, status: 'completed' }));
                              setStatusDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                          >
                            Completed
                          </div>
                          <div
                            onClick={() => {
                              setFormData(prev => ({ ...prev, status: 'on-hold' }));
                              setStatusDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                          >
                            On Hold
                          </div>
                          <div
                            onClick={() => {
                              setFormData(prev => ({ ...prev, status: 'cancelled' }));
                              setStatusDropdownOpen(false);
                            }}
                            className="px-4 py-3 hover:bg-gray-100 cursor-pointer"
                          >
                            Cancelled
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-xs font-semibold text-gray-900 mb-0.5">
                    Description *
                  </label>
                  <input
                    type="text"
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition-all ${
                      errors.description ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter contract description"
                    required
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                  )}
                </div>

                {/* Auto-calculated Amount (read-only) */}
                <div>
                  <label className="block text-xs font-semibold text-gray-900 mb-0.5">
                    Total Amount (auto)
                  </label>
                  <div className="w-full px-2 py-1 border border-gray-200 rounded text-xs font-semibold bg-gray-50">
                    ${Array.isArray(contractItems) && contractItems.length > 0
                      ? contractItems.reduce((sum, it) => sum + (parseFloat(it.total_amount || 0) || 0), 0).toFixed(2)
                      : '0.00'}
                  </div>
                </div>

                <div>
                  <label htmlFor="start_date" className="block text-xs font-semibold text-gray-900 mb-0.5">
                    Start Date
                  </label>
                  <input
                    type="date"
                    id="start_date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition-all ${
                      errors.start_date ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {errors.start_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="end_date" className="block text-xs font-semibold text-gray-900 mb-0.5">
                    End Date
                  </label>
                  <input
                    type="date"
                    id="end_date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    className={`w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition-all ${
                      errors.end_date ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {errors.end_date && (
                    <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="notes" className="block text-xs font-semibold text-gray-900 mb-0.5">
                    Notes
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-gray-800 focus:border-transparent transition-all resize-none"
                    placeholder="Enter additional notes"
                  />
                </div>
              </div>

              {/* Option: Create items from budget lines - show if project is selected */}
              {formData.project_vuid && (
                <div className="mt-2">
                  <label className="inline-flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={createItemsFromBudget}
                      onChange={(e) => setCreateItemsFromBudget(e.target.checked)}
                      className="h-5 w-5 text-gray-800 border-gray-300 rounded focus:ring-gray-800"
                    />
                    <span className="text-gray-900 text-md font-medium">
                      Create Contract Items based on Budget lines
                    </span>
                  </label>
                  <p className="text-sm text-gray-500 mt-1">
                    When selected, a contract item will be created for each project budget line that has an amount, with matching cost code and cost type, amount prefilled, and line numbers auto-incremented starting at 0001. Budget lines with a $0 amount are skipped.
                    {projectBudgetLinesLoading ? (
                      <span className="block mt-1 text-gray-500 font-medium">
                        Loading budget lines…
                      </span>
                    ) : projectBudgetLines.some(budgetLineHasAmount) ? (
                      <span className="block mt-1 text-green-600 font-medium">
                        ✓ {projectBudgetLines.filter(budgetLineHasAmount).length} budget lines will be copied
                        {projectBudgetLines.length - projectBudgetLines.filter(budgetLineHasAmount).length > 0 && (
                          <span className="block text-gray-500 font-normal">
                            {projectBudgetLines.length - projectBudgetLines.filter(budgetLineHasAmount).length} zero-dollar lines will be skipped
                          </span>
                        )}
                      </span>
                    ) : projectBudgetLines.length > 0 ? (
                      <span className="block mt-1 text-orange-600 font-medium">
                        ⚠ Budget lines were found, but every amount is $0 — items will need to be created manually
                      </span>
                    ) : (
                      <span className="block mt-1 text-orange-600 font-medium">
                        ⚠ No budget lines found for this project - items will need to be created manually
                      </span>
                    )}
                    {projectSettings.allow_project_cost_codes === 'true' && (
                      <span className="block mt-1 text-blue-600 font-medium">
                        Project-specific cost codes are enabled - any cost code format will be supported.
                      </span>
                    )}
                  </p>
                  
                  {createItemsFromBudget && projectBudgetLines.some(budgetLineHasAmount) && (
                    <div className="mt-3 ml-8">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Budget Increase Percentage (Optional)
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={budgetIncreasePercentage}
                          onChange={(e) => setBudgetIncreasePercentage(parseFloat(e.target.value) || 0)}
                          min="0"
                          max="200"
                          step="0.1"
                          placeholder="0.0"
                          className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                        <span className="text-gray-600">%</span>
                        <span className="text-sm text-gray-500">
                          (e.g., 15% for contractor fee)
                        </span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Contract amounts will be: Budget Amount × (1 {budgetIncreasePercentage}%)
                        {budgetIncreasePercentage > 0 && (
                          <span className="text-blue-600 font-medium">
                            {' '}= Budget Amount × {(1 + budgetIncreasePercentage / 100).toFixed(3)}
                          </span>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-center space-x-4">
                <button
                  type="submit"
                  className="btn-claret"
                  >
                  {editingContract ? 'Update Contract' : 'Create Contract'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn-claret"
                  >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Allocations Modal */}
        {}
        {showAllocationsModal && selectedItemForAllocations && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">
                  Manage Allocations for Item: {selectedItemForAllocations.item_number}
                </h2>
                <button
                  onClick={() => setShowAllocationsModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
                >
                  ×
                </button>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  Allocate this contract item across multiple cost codes and cost types.
                </p>

                {/* Current Allocations */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Current Allocations</h3>
                  {allocations.length === 0 ? (
                    <p className="text-gray-500 italic">No allocations defined yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {allocations.map((allocation) => (
                        <div key={allocation.vuid} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">
                              {getAllCostCodes().find(c => c.vuid === allocation.cost_code_vuid)?.code} - 
                              {costTypes.find(t => t.vuid === allocation.cost_type_vuid)?.cost_type}
                            </div>
                            <div className="text-sm text-gray-600">
                              {getAllCostCodes().find(c => c.vuid === allocation.cost_code_vuid)?.description} - 
                              {costTypes.find(t => t.vuid === allocation.cost_type_vuid)?.description}
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <button
                              onClick={() => handleDeleteAllocation(allocation.vuid)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Add New Allocation */}
                {!showAddAllocationForm ? (
                  <button
                    onClick={() => setShowAddAllocationForm(true)}
                    className="btn-claret"
                    >
                    Add Allocation
                  </button>
                ) : (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-800">
                        <strong>Note:</strong> Only unallocated cost code/type combinations from the project budget and actual costs (AP invoices, labor costs, project expenses) are shown. This prevents duplicate allocations across all contract items in this project.
                      </p>
                    </div>
                    <h4 className="text-md font-semibold text-gray-900 mb-3">Add New Allocation</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cost Code</label>
                        <select
                          value={newAllocation.cost_code_vuid}
                          onChange={(e) => setNewAllocation({...newAllocation, cost_code_vuid: e.target.value, cost_type_vuid: ''})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="">Select Cost Code</option>
                          {(() => {
                            // Get all allocated cost code/type combinations for this project
                            const contract = contracts.find(c => c.vuid === selectedItemForAllocations?.contract_vuid);
                            const allProjectAllocations = contract?.items ? 
                              contract.items.flatMap(item => allocationsByItem[item.vuid] || []).map(alloc => 
                                `${alloc.cost_code_vuid}_${alloc.cost_type_vuid}`
                              ) : [];
                            
                            // Get unallocated combinations from project budget
                            const unallocatedCombinationsFromBudget = projectBudgetLines.length === 0 ? [] :
                              projectBudgetLines.filter(budgetLine => 
                                budgetLine.cost_code_vuid && budgetLine.cost_type_vuid &&
                                !allProjectAllocations.includes(`${budgetLine.cost_code_vuid}_${budgetLine.cost_type_vuid}`)
                              );
                            
                            // Get unallocated combinations from actual costs (AP invoices, labor, expenses)
                            const unallocatedCombinationsFromCosts = unallocatedCosts.filter(cost =>
                              cost.cost_code_vuid && cost.cost_type_vuid &&
                              !allProjectAllocations.includes(`${cost.cost_code_vuid}_${cost.cost_type_vuid}`)
                            );
                            
                            // Combine both sources
                            const allUnallocatedCombinations = [
                              ...unallocatedCombinationsFromBudget,
                              ...unallocatedCombinationsFromCosts
                            ];
                            
                            // Get unique cost codes from all unallocated combinations
                            const unallocatedCostCodeVuids = new Set(
                              allUnallocatedCombinations.map(item => item.cost_code_vuid).filter(Boolean)
                            );
                            
                            return contractCostCodeOptions()
                              .filter(costCode => 
                                projectBudgetLines.length === 0 && unallocatedCosts.length === 0 ||
                                unallocatedCostCodeVuids.has(costCode.vuid)
                              )
                              .map((costCode) => (
                                <option key={costCode.vuid} value={costCode.vuid}>
                                  {costCode.code} - {costCode.description}
                                </option>
                              ));
                          })()}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Cost Type</label>
                        <select
                          value={newAllocation.cost_type_vuid}
                          onChange={(e) => setNewAllocation({...newAllocation, cost_type_vuid: e.target.value})}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={!newAllocation.cost_code_vuid}
                        >
                          <option value="">{newAllocation.cost_code_vuid ? 'Select Cost Type' : 'Select Cost Code First'}</option>
                          {(() => {
                            if (!newAllocation.cost_code_vuid) return [];
                            
                            // Get all allocated cost code/type combinations for this project
                            const contract = contracts.find(c => c.vuid === selectedItemForAllocations?.contract_vuid);
                            const allProjectAllocations = contract?.items ? 
                              contract.items.flatMap(item => allocationsByItem[item.vuid] || []).map(alloc => 
                                `${alloc.cost_code_vuid}_${alloc.cost_type_vuid}`
                              ) : [];
                            
                            // Get unallocated combinations from project budget for the selected cost code
                            const unallocatedCombinationsFromBudget = projectBudgetLines.length === 0 ? [] :
                              projectBudgetLines.filter(budgetLine => 
                                budgetLine.cost_code_vuid === newAllocation.cost_code_vuid &&
                                budgetLine.cost_type_vuid &&
                                !allProjectAllocations.includes(`${budgetLine.cost_code_vuid}_${budgetLine.cost_type_vuid}`)
                              );
                            
                            // Get unallocated combinations from actual costs for the selected cost code
                            const unallocatedCombinationsFromCosts = unallocatedCosts.filter(cost =>
                              cost.cost_code_vuid === newAllocation.cost_code_vuid &&
                              cost.cost_type_vuid &&
                              !allProjectAllocations.includes(`${cost.cost_code_vuid}_${cost.cost_type_vuid}`)
                            );
                            
                            // Combine cost types from both sources
                            const unallocatedCostTypeVuidsFromBudget = new Set(
                              unallocatedCombinationsFromBudget.map(bl => bl.cost_type_vuid).filter(Boolean)
                            );
                            const unallocatedCostTypeVuidsFromCosts = new Set(
                              unallocatedCombinationsFromCosts.map(cost => cost.cost_type_vuid).filter(Boolean)
                            );
                            
                            // Combine both sets
                            const allUnallocatedCostTypeVuids = new Set([
                              ...unallocatedCostTypeVuidsFromBudget,
                              ...unallocatedCostTypeVuidsFromCosts
                            ]);
                            
                            // Show cost types that are either:
                            // 1. In unallocated budget combinations, OR
                            // 2. In unallocated actual costs, OR
                            // 3. If no budget/costs, show all active types
                            return costTypes
                              .filter(isCostTypeSelectable)
                              .filter(costType => {
                                // Check if this combination is already allocated
                                const combinationKey = `${newAllocation.cost_code_vuid}_${costType.vuid}`;
                                const isAlreadyAllocated = allProjectAllocations.includes(combinationKey);
                                if (isAlreadyAllocated) return false;
                                
                                // If no constraints, show all
                                if (projectBudgetLines.length === 0 && unallocatedCosts.length === 0) {
                                  return true;
                                }
                                
                                // Show if in unallocated combinations (from budget or costs)
                                return allUnallocatedCostTypeVuids.has(costType.vuid);
                              })
                              .map((costType) => (
                                <option key={costType.vuid} value={costType.vuid}>
                                  {costType.cost_type} ({costType.abbreviation})
                                </option>
                              ));
                          })()}
                        </select>
                      </div>
                    </div>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleAddAllocation}
                        className="btn-claret"
                        >
                        Add Allocation
                      </button>
                      <button
                        onClick={() => setShowAddAllocationForm(false)}
                        className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-4 rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowAllocationsModal(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-semibold py-2 px-6 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAllocations}
                  className="btn-claret"
                  >
                  Save Allocations
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Filters — only on the all-projects list */}
        {!showCreateForm && !showItemsForm && !new URLSearchParams(location.search).get('project') && (
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8 border border-gray-200">
          <div className="flex flex-wrap gap-4">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Search contracts..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-vermillion-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Project Filter */}
            <div className="relative dropdown-container">
              <button
                onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-vermillion-500"
              >
                Project: {projectFilter === 'all' ? 'All' : projects.find(p => p.vuid === projectFilter)?.project_name || 'Unknown'}
                <svg className="inline ml-2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {projectDropdownOpen && (
                <div className="absolute z-10 mt-1 w-48 bg-white rounded-lg shadow-lg border-2 border-gray-300">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setProjectFilter('all');
                        setProjectDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      All Projects
                    </button>
                    {projects.map(project => (
                      <button
                        key={project.vuid}
                        onClick={() => {
                          setProjectFilter(project.vuid);
                          setProjectDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                      >
                        {project.project_number} - {project.project_name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Status Filter */}
            <div className="relative dropdown-container">
              <button
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-vermillion-500 focus:border-vermillion-500"
              >
                Status: {statusFilter === 'all' ? 'All' : statusFilter}
                <svg className="inline ml-2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {statusDropdownOpen && (
                <div className="absolute z-10 mt-1 w-48 bg-white rounded-lg shadow-lg border-2 border-gray-300">
                  <div className="py-1">
                    <button
                      onClick={() => {
                        setStatusFilter('all');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      All
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('active');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      Active
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('completed');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      Completed
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('cancelled');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      Cancelled
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter('on_hold');
                        setStatusDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-900 hover:bg-gray-100 transition-colors"
                    >
                      On Hold
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Contracts List */}
        {!showCreateForm && !showItemsForm && (
        <div className="bg-white rounded-lg shadow border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Project Contracts</h2>
          </div>

          {paginatedContracts.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500">
              No contracts found matching your criteria.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Number</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Original Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Change Orders</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">New Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedContracts.map((contract) => (
                    <tr key={contract.vuid} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {projects.find(p => p.vuid === contract.project_vuid)?.project_name || 'Unknown'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {contract.contract_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {contract.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {customers.find(c => c.vuid === contract.customer_vuid)?.customer_name || 'Not specified'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          {CONTRACT_TYPES.find(type => type.value === contract.contract_type)?.label || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          contract.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                        <div className="flex flex-col">
                          <span>{formatCurrency(contract.contract_amount)}</span>
                          <span className="text-xs text-gray-500 font-normal">Original</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                        {(() => {
                          const changeOrderTotal = getContractChangeOrderTotal(contract.vuid);
                          return (
                            <div className="flex flex-col">
                              <span className={changeOrderTotal !== 0 ? 'text-green-600' : 'text-gray-900'}>
                                {changeOrderTotal > 0 ? '+' : ''}{formatCurrency(changeOrderTotal)}
                              </span>
                              <span className="text-xs text-gray-500 font-normal">Change Orders</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                        {(() => {
                          const originalAmount = parseFloat(contract.contract_amount) || 0;
                          const changeOrderTotal = getContractChangeOrderTotal(contract.vuid);
                          const newAmount = originalAmount + changeOrderTotal;
                          return (
                            <div className="flex flex-col">
                              <span className={newAmount !== originalAmount ? 'text-blue-600' : 'text-gray-900'}>
                                {formatCurrency(newAmount)}
                              </span>
                              <span className="text-xs text-gray-500 font-normal">New Amount</span>
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => selectedContract && selectedContract.vuid === contract.vuid && showItemsForm ? handleHideItems() : handleViewItems(contract)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip={selectedContract && selectedContract.vuid === contract.vuid && showItemsForm ? 'Hide Items' : 'View Items'}
                            >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedContract(contract);
                              setShowFeeSettings(true);
                            }}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Manage fee settings"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => {
                              setSelectedContract(contract);
                              setShowContractHistory(true);
                            }}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="View change history"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleIntegrationModal(contract)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Send to integration"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleEditContract(contract)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Edit contract"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteContract(contract)}
                            className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                            data-tooltip="Delete contract"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {filteredContracts.length > contractsPerPage && (
            <div className="px-6 py-4 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  Showing {((currentPage - 1) * contractsPerPage) + 1} to {Math.min(currentPage * contractsPerPage, filteredContracts.length)} of {filteredContracts.length} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage >= Math.ceil(filteredContracts.length / contractsPerPage)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        <div>
          {/* Contract Items Form - MOVED HERE */}
          {showItemsForm && selectedContract && (
            <div className="budget-lines-panel">
              <div className="budget-lines-header">
                <h2>Contract Items</h2>
                <p>
                  Tab moves between fields, Enter adds a line, and start typing in dropdowns to search.
                </p>
              </div>
              
              {/* Success Message */}
              {successMessage && (
                <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-600 font-medium">{successMessage}</p>
                </div>
              )}
              
              {/* Percentage Adjustment Controls */}
              {contractItems.length > 0 && (
                <div className="mb-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <div className={`flex items-center justify-between ${showPercentageAdjustment ? 'mb-3' : ''}`}>
                    <h3 className="text-sm font-semibold text-gray-900">Mark-up Adjustments</h3>
                    <button
                      onClick={() => setShowPercentageAdjustment(!showPercentageAdjustment)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      {showPercentageAdjustment ? 'Hide' : 'Show'} Adjustment Tools
                    </button>
                  </div>
                  
                  {showPercentageAdjustment && (
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4">
                        <label className="text-sm font-medium text-gray-700">
                          Mark-up Percentage:
                        </label>
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            value={adjustmentPercentage}
                            onChange={(e) => setAdjustmentPercentage(parseFloat(e.target.value) || 0)}
                            min="-50"
                            max="200"
                            step="0.1"
                            placeholder="0.0"
                            className="w-20 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <span className="text-gray-600">%</span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={applyPercentageAdjustment}
                            disabled={adjustmentPercentage === 0}
                            className="btn-claret"
                            >
                            Apply to All Items
                          </button>
                          {Object.keys(originalAmounts).length > 0 && (
                            <button
                              onClick={resetToOriginalAmounts}
                              className="btn-claret"
                              >
                              Reset to Original
                            </button>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-sm text-gray-600">
                        <p>
                          <strong>Current calculation:</strong> Original Amount × (1 {adjustmentPercentage}%)
                          {adjustmentPercentage !== 0 && (
                            <span className="text-blue-600 font-medium">
                              {' '}= Original Amount × {(1 + adjustmentPercentage / 100).toFixed(3)}
                            </span>
                          )}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          This will update all contract item amounts. Use positive percentages for markups (e.g., 15%) or negative for discounts (e.g., -5%).
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              <div className="budget-lines-toolbar">
                <button
                  type="button"
                  onClick={handleAddNewItem}
                  className="btn-claret"
                  data-tooltip="Jump to the blank row. If that row is filled in, this saves it."
                >
                  Add Item
                </button>
              </div>

              {/* Loading indicator for budget lines processing */}
              {budgetLinesProgress.total > 0 && budgetLinesProgress.current < budgetLinesProgress.total && (
                <div className="mb-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <div className="flex-1">
                      <p className="text-lg font-semibold text-blue-900">
                        Creating contract items from budget lines...
                      </p>
                      <p className="text-sm text-blue-700 mt-1">
                        Processing {budgetLinesProgress.current} of {budgetLinesProgress.total} budget lines
                      </p>
                      <div className="mt-2 w-full bg-blue-200 rounded-full h-2.5">
                        <div 
                          className="bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${(budgetLinesProgress.current / budgetLinesProgress.total) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="budget-lines-grid contract-items-grid">
                <table key={`contract-items-${projectCostCodes.length}`} className="budget-lines-table contract-items-table" style={{ position: 'relative' }}>
                  <thead>
                    <tr>
                      <th className="contract-col-item budget-lines-sortable" onClick={() => toggleContractItemSort('item_number')}>Item *{contractItemSortMark('item_number')}</th>
                      <th className="contract-col-code budget-lines-sortable" onClick={() => toggleContractItemSort('cost_code')}>Code{contractItemSortMark('cost_code')}</th>
                      <th className="contract-col-type budget-lines-sortable" onClick={() => toggleContractItemSort('cost_type')}>Type{contractItemSortMark('cost_type')}</th>
                      {usesLineMarkups(feeLevel) && (
                        <th className="contract-col-markup budget-lines-sortable" onClick={() => toggleContractItemSort('markup')}>Markup{contractItemSortMark('markup')}</th>
                      )}
                      {usesLineMarkups(feeLevel) && (
                        <th className="contract-col-labor budget-lines-sortable" onClick={() => toggleContractItemSort('labor_fee')} data-tooltip="Fee on labor schedule">Labor{contractItemSortMark('labor_fee')}</th>
                      )}
                      <th className="contract-col-desc budget-lines-sortable" onClick={() => toggleContractItemSort('description')}>Description *{contractItemSortMark('description')}</th>
                      <th className="budget-lines-amount budget-lines-sortable" onClick={() => toggleContractItemSort('total_amount')}>Amount *{contractItemSortMark('total_amount')}</th>
                      {showContractChangeColumns && (
                        <th className="budget-lines-amount budget-lines-sortable" onClick={() => toggleContractItemSort('change_orders')}>Changes{contractItemSortMark('change_orders')}</th>
                      )}
                      {showContractChangeColumns && (
                        <th className="budget-lines-amount budget-lines-sortable" onClick={() => toggleContractItemSort('new_amount')}>Revised{contractItemSortMark('new_amount')}</th>
                      )}
                      {showContractNotesColumn && (
                        <th className="budget-lines-sortable" onClick={() => toggleContractItemSort('notes')}>Notes{contractItemSortMark('notes')}</th>
                      )}
                      <th className="budget-lines-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {}
                    {contractItemsLoading && (!contractItems || contractItems.length === 0) ? (
                      <tr>
                        <td colSpan={contractItemColumnCount} className="text-center py-8 text-gray-500">
                          <div className="flex flex-col items-center space-y-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <p className="text-lg font-semibold text-blue-900">Loading contract items...</p>
                          </div>
                        </td>
                      </tr>
                    ) : contractItems && contractItems.length > 0 ? (
                      sortedContractItems.map((item) => {
                          const index = contractItems.findIndex((row) => row.vuid === item.vuid);
                          return (
                          <tr key={item.vuid || index} id={item.vuid ? `contract-item-${item.vuid}` : undefined}>
                            <td className="contract-col-item">
                              <input
                                type="text"
                                value={item.item_number || ''}
                                onChange={(e) => {
                                  const newItems = [...contractItems];
                                  newItems[index] = { ...newItems[index], item_number: e.target.value };
                                  setContractItems(newItems);
                                }}
                                className="budget-lines-input"
                                placeholder="Item #"
                              />
                            </td>
                            <td className="contract-col-code">
                              <div className="budget-lines-code-cell">
                                <div className="budget-lines-code-input">
                                  <BudgetStylePicker
                                    open={costCodeDropdownOpen === item.vuid}
                                    onOpen={() => {
                                      setCostCodeDropdownOpen(item.vuid);
                                      setCostTypeDropdownOpen(null);
                                    }}
                                    displayValue={findContractCostCode(item.cost_code_vuid)?.code || ''}
                                    placeholder="Type to search..."
                                    searchPlaceholder="Search cost codes..."
                                    options={costCodeDropdownOpen === item.vuid ? costCodeMenuOptions() : []}
                                    onSelect={(vuid) => selectContractItemCostCode(item, index, vuid)}
                                  />
                                </div>
                                {findContractCostCode(item.cost_code_vuid)?.description ? (
                                  <span className="budget-lines-code-desc" title={findContractCostCode(item.cost_code_vuid).description}>
                                    {findContractCostCode(item.cost_code_vuid).description}
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className="contract-col-type">
                              <BudgetStylePicker
                                layout="inline"
                                open={costTypeDropdownOpen === item.vuid}
                                onOpen={() => {
                                  setCostTypeDropdownOpen(item.vuid);
                                  setCostCodeDropdownOpen(null);
                                }}
                                displayValue={costTypeDisplay(item.cost_type_vuid)}
                                placeholder="Select cost type(s)..."
                                searchPlaceholder="Search cost types..."
                                options={costTypeDropdownOpen === item.vuid ? costTypeMenuOptions(item.cost_code_vuid, item.cost_type_vuid) : []}
                                onSelect={(vuid) => selectContractItemCostType(item, index, vuid)}
                              />
                            </td>
                            {usesLineMarkups(feeLevel) && (
                              <td className="contract-col-markup">
                                {showLaborScheduleFee(item.cost_code_vuid, item.cost_type_vuid) && !item.apply_fee_to_labor_schedule ? (
                                  <span className="text-gray-400 text-sm">—</span>
                                ) : (
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={markupDrafts[item.vuid] !== undefined ? markupDrafts[item.vuid] : itemMarkupValue(item)}
                                    onChange={(e) => setMarkupDrafts((prev) => ({ ...prev, [item.vuid]: e.target.value }))}
                                    onBlur={(e) => saveInlineMarkup(item, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        e.preventDefault();
                                        e.currentTarget.blur();
                                      }
                                    }}
                                    className="budget-lines-input"
                                    placeholder="0.0"
                                  />
                                )}
                              </td>
                            )}
                            {usesLineMarkups(feeLevel) && (
                              <td className="contract-col-labor">
                                {showLaborScheduleFee(item.cost_code_vuid, item.cost_type_vuid) ? (
                                  <label className="inline-flex items-center text-sm text-gray-700" data-tooltip="Add fee to labor schedule">
                                    <input
                                      type="checkbox"
                                      aria-label="Add fee to labor schedule"
                                      checked={Boolean(item.apply_fee_to_labor_schedule)}
                                      onChange={async (e) => {
                                        const checked = e.target.checked;
                                        const newItems = [...contractItems];
                                        newItems[index] = { ...newItems[index], apply_fee_to_labor_schedule: checked };
                                        setContractItems(newItems);
                                        if (item.vuid && !String(item.vuid).startsWith('temp-')) {
                                          try {
                                            await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
                                              apply_fee_to_labor_schedule: checked
                                            });
                                          } catch (err) {
                                            console.warn(`Failed to update labor schedule fee toggle for item ${item.item_number}:`, err);
                                          }
                                        }
                                      }}
                                    />
                                  </label>
                                ) : (
                                  <span className="text-gray-400 text-sm">—</span>
                                )}
                              </td>
                            )}
                            <td className="contract-col-desc">
                              <textarea
                                value={item.description || ''}
                                onChange={(e) => {
                                  const newItems = [...contractItems];
                                  newItems[index] = { ...newItems[index], description: e.target.value };
                                  setContractItems(newItems);
                                }}
                                className="budget-lines-input budget-lines-grow"
                                placeholder="Description"
                                rows={1}
                                title={item.description || ''}
                              />
                            </td>
                            <td className="budget-lines-amount">
                              <input
                                type="text"
                                value={item.total_amount || ''}
                                onChange={(e) => {
                                  const newItems = [...contractItems];
                                  newItems[index] = { ...newItems[index], total_amount: e.target.value };
                                  setContractItems(newItems);
                                }}
                                className="budget-lines-input budget-lines-amount-input"
                                placeholder="0.00"
                              />
                            </td>
                            {showContractChangeColumns && (
                            <td className="budget-lines-amount">
                              {(() => {
                                const changeOrderAmount = getChangeOrderAmount(item);
                                const matchingLines = changeOrderLines.filter(changeLine =>
                                  changeLine.cost_code_vuid === item.cost_code_vuid &&
                                  changeLine.cost_type_vuid === item.cost_type_vuid
                                );
                                const countLabel = matchingLines.length
                                  ? `${matchingLines.length} change order${matchingLines.length !== 1 ? 's' : ''}`
                                  : '';
                                return changeOrderAmount !== 0 ? (
                                  <span className={`text-sm font-medium ${changeOrderAmount > 0 ? 'text-green-600' : 'text-red-600'}`} title={countLabel}>
                                    {changeOrderAmount > 0 ? '+' : ''}{formatCurrency(changeOrderAmount)}
                                  </span>
                                ) : (
                                  <span className="text-sm text-gray-400">—</span>
                                );
                              })()}
                            </td>
                            )}
                            {showContractChangeColumns && (
                            <td className="budget-lines-amount">
                              {(() => {
                                const originalAmount = parseFloat(item.total_amount || 0) || 0;
                                const changeOrderAmount = getChangeOrderAmount(item);
                                const totalWithChanges = originalAmount + changeOrderAmount;
                                return (
                                  <span className={`text-sm font-semibold ${totalWithChanges !== originalAmount ? 'text-blue-600' : 'text-gray-900'}`}>
                                    {formatCurrency(totalWithChanges)}
                                  </span>
                                );
                              })()}
                            </td>
                            )}
                            {showContractNotesColumn && (
                            <td>
                              <textarea
                                value={item.notes || ''}
                                onChange={(e) => {
                                  const newItems = [...contractItems];
                                  newItems[index] = { ...newItems[index], notes: e.target.value };
                                  setContractItems(newItems);
                                }}
                                className="budget-lines-input budget-lines-grow"
                                placeholder="Notes"
                                rows={1}
                                title={item.notes || ''}
                              />
                            </td>
                            )}
                            <td className="budget-lines-actions">
                              <div className="flex space-x-1 justify-end">
                                {!showContractNotesColumn && (
                                  <button
                                    type="button"
                                    onClick={() => setNotesColumnOpen(true)}
                                    className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                                    data-tooltip="Add a note"
                                    aria-label="Add a note"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  onClick={() => handleManageAllocations(item)}
                                  className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                                  data-tooltip="Manage cost code and cost type allocations"
                                  aria-label="Allocations"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                  </svg>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDeleteItem(item.vuid)}
                                  className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                                  data-tooltip="Delete contract item"
                                  aria-label="Delete"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                  </svg>
                                </button>
                              </div>
                            </td>
                          </tr>
                          );
                        })
                    ) : (
                      <tr>
                        <td colSpan={contractItemColumnCount} className="text-center py-8 text-gray-500">
                          {budgetLinesProgress.total > 0 && budgetLinesProgress.current < budgetLinesProgress.total ? (
                            <div className="flex flex-col items-center space-y-2">
                              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                              <p className="text-lg font-semibold text-blue-900">
                                Creating contract items from budget lines...
                              </p>
                              <p className="text-sm text-blue-700">
                                {budgetLinesProgress.current} of {budgetLinesProgress.total} items created
                              </p>
                            </div>
                          ) : (
                            'No contract items found. Add your first item below.'
                          )}
                        </td>
                      </tr>
                    )}
                    {/* New item row */}
                    <tr id="new-contract-item-row" className="budget-lines-new-row">
                      <td className="contract-col-item">
                        <input
                          type="text"
                          id="new-item-number"
                          value={newItemData.item_number}
                          onChange={(e) => setNewItemData({...newItemData, item_number: e.target.value})}
                          className="budget-lines-input"
                          placeholder="Item #"
                        />
                      </td>
                      <td className="contract-col-code">
                        <div className="budget-lines-code-cell">
                          <div className="budget-lines-code-input">
                        <BudgetStylePicker
                          open={newItemCostCodeDropdownOpen}
                          onOpen={() => {
                            setNewItemCostCodeDropdownOpen(true);
                            setNewItemCostTypeDropdownOpen(false);
                          }}
                          displayValue={findContractCostCode(newItemData.cost_code_vuid)?.code || ''}
                          placeholder="Type to search..."
                          searchPlaceholder="Search cost codes..."
                          options={newItemCostCodeDropdownOpen ? costCodeMenuOptions() : []}
                          onSelect={(vuid) => {
                            const next = {
                              ...newItemData,
                              cost_code_vuid: vuid,
                              apply_fee_to_labor_schedule: false
                            };
                            const typeOptions = contractCostTypeOptions(vuid, '');
                            if (typeOptions.length === 1) {
                              next.cost_type_vuid = typeOptions[0].vuid;
                              if (usesLineMarkups(feeLevel)) {
                                next.markup_percentage = getMarkupFromFeeSettings(vuid, typeOptions[0].vuid) ?? 0;
                              }
                            } else if (!typeOptions.some((costType) => costType.vuid === newItemData.cost_type_vuid)) {
                              next.cost_type_vuid = '';
                            }
                            setNewItemData(next);
                            setNewItemCostCodeDropdownOpen(false);
                          }}
                        />
                          </div>
                          {findContractCostCode(newItemData.cost_code_vuid)?.description ? (
                            <span className="budget-lines-code-desc" title={findContractCostCode(newItemData.cost_code_vuid).description}>
                              {findContractCostCode(newItemData.cost_code_vuid).description}
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="contract-col-type">
                        <BudgetStylePicker
                          layout="inline"
                          open={newItemCostTypeDropdownOpen}
                          onOpen={() => {
                            setNewItemCostTypeDropdownOpen(true);
                            setNewItemCostCodeDropdownOpen(false);
                          }}
                          displayValue={costTypeDisplay(newItemData.cost_type_vuid)}
                          placeholder="Select cost type(s)..."
                          searchPlaceholder="Search cost types..."
                          options={newItemCostTypeDropdownOpen ? costTypeMenuOptions(newItemData.cost_code_vuid, newItemData.cost_type_vuid) : []}
                          onSelect={(vuid) => {
                            const next = { ...newItemData, cost_type_vuid: vuid };
                            if (usesLineMarkups(feeLevel) && newItemData.cost_code_vuid && vuid) {
                              next.markup_percentage = getMarkupFromFeeSettings(newItemData.cost_code_vuid, vuid) ?? 0;
                            }
                            setNewItemData(next);
                            setNewItemCostTypeDropdownOpen(false);
                          }}
                        />
                      </td>
                      {usesLineMarkups(feeLevel) && (
                      <td className="contract-col-markup">
                        {showLaborScheduleFee(newItemData.cost_code_vuid, newItemData.cost_type_vuid) && !newItemData.apply_fee_to_labor_schedule ? (
                          <span className="text-gray-400 text-sm">—</span>
                        ) : (
                          <input
                            type="number"
                            step="0.1"
                            value={newItemData.markup_percentage ?? 0}
                            onChange={(e) => setNewItemData({...newItemData, markup_percentage: e.target.value})}
                            className="budget-lines-input"
                            placeholder="0.0"
                          />
                        )}
                      </td>
                      )}
                      {usesLineMarkups(feeLevel) && (
                      <td className="contract-col-labor">
                        {showLaborScheduleFee(newItemData.cost_code_vuid, newItemData.cost_type_vuid) ? (
                          <label className="inline-flex items-center text-sm text-gray-700" data-tooltip="Add fee to labor schedule">
                            <input
                              type="checkbox"
                              aria-label="Add fee to labor schedule"
                              checked={Boolean(newItemData.apply_fee_to_labor_schedule)}
                              onChange={(e) => setNewItemData({
                                ...newItemData,
                                apply_fee_to_labor_schedule: e.target.checked
                              })}
                            />
                          </label>
                        ) : (
                          <span className="text-gray-400 text-sm">—</span>
                        )}
                      </td>
                      )}
                      <td className="contract-col-desc">
                        <textarea
                          value={newItemData.description}
                          onChange={(e) => setNewItemData({...newItemData, description: e.target.value})}
                          className="budget-lines-input budget-lines-grow"
                          placeholder="Description"
                          rows={1}
                        />
                      </td>
                      <td className="budget-lines-amount">
                        <input
                          type="text"
                          value={newItemData.total_amount}
                          onChange={(e) => setNewItemData({...newItemData, total_amount: e.target.value})}
                          className="budget-lines-input budget-lines-amount-input"
                          placeholder="0.00"
                        />
                      </td>
                      {showContractChangeColumns && <td className="budget-lines-amount"></td>}
                      {showContractChangeColumns && <td className="budget-lines-amount"></td>}
                      {showContractNotesColumn && (
                      <td>
                        <textarea
                          value={newItemData.notes}
                          onChange={(e) => setNewItemData({...newItemData, notes: e.target.value})}
                          className="budget-lines-input budget-lines-grow"
                          placeholder="Notes"
                          rows={1}
                        />
                      </td>
                      )}
                      <td className="budget-lines-actions">
                        <div className="flex space-x-1 justify-end">
                          {!showContractNotesColumn && (
                            <button
                              type="button"
                              onClick={() => setNotesColumnOpen(true)}
                              className="text-gray-900 hover:text-gray-700 p-1 hover:bg-gray-100 rounded transition-colors"
                              data-tooltip="Add a note"
                              aria-label="Add a note"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={handleAddNewItem}
                            className="btn-claret"
                            data-tooltip="Save this new line"
                          >
                            Save
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="budget-lines-footer">
                <button
                  type="button"
                  onClick={handleDoneEditing}
                  className="btn-claret"
                  data-tooltip="Save the new line and any edits, then close"
                  >
                  Done
                </button>
                <button
                  type="button"
                  onClick={handleSaveAllItems}
                  className="btn-claret"
                  data-tooltip="Save the new line and any edits to existing lines"
                  >
                  Save All Items
                </button>
              </div>
            </div>
          )}

          {/* Change Order Lines Section */}
          {changeOrderLines && changeOrderLines.length > 0 && (
            <div className="budget-lines-panel">
              <div className="budget-lines-header">
                <h2>Change Order Lines</h2>
              </div>
              <div className="budget-lines-grid">
                <table className="budget-lines-table">
                  <thead>
                    <tr>
                      <th>Change Order</th>
                      <th>Cost Code</th>
                      <th>Cost Type</th>
                      <th>Description</th>
                      <th className="budget-lines-amount">Contract Change</th>
                      <th className="budget-lines-amount">Budget Change</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeOrderLines.map((changeLine, index) => {
                      const costCode = getAllCostCodes().find(c => c.vuid === changeLine.cost_code_vuid);
                      const costType = costTypes.find(t => t.vuid === changeLine.cost_type_vuid);
                      const changeOrder = externalChangeOrders.find(co => co.vuid === changeLine.change_order_vuid);
                      
                      return (
                        <tr key={`change-${changeLine.vuid || index}`}>
                          <td>
                            <div className="font-medium">{changeLine.change_order_number || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{changeLine.change_order_date || ''}</div>
                          </td>
                          <td>
                            {costCode ? `${costCode.code} - ${costCode.description}` : 'N/A'}
                          </td>
                          <td>
                            {costType ? `${costType.abbreviation} - ${costType.cost_type}` : 'N/A'}
                          </td>
                          <td>
                            {changeLine.notes || 'No description'}
                          </td>
                          <td className="budget-lines-amount">
                            <span className={`font-medium ${(parseFloat(changeLine.contract_amount_change || 0) >= 0) ? 'text-green-600' : 'text-red-600'}`}>
                              {(parseFloat(changeLine.contract_amount_change || 0) >= 0) ? '+' : ''}{formatCurrency(changeLine.contract_amount_change || 0)}
                            </span>
                          </td>
                          <td className="budget-lines-amount">
                            <span className={`font-medium ${(parseFloat(changeLine.budget_amount_change || 0) >= 0) ? 'text-green-600' : 'text-red-600'}`}>
                              {(parseFloat(changeLine.budget_amount_change || 0) >= 0) ? '+' : ''}{formatCurrency(changeLine.budget_amount_change || 0)}
                            </span>
                          </td>
                          <td>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              changeLine.change_order_status === 'approved' ? 'bg-green-100 text-green-800' :
                              changeLine.change_order_status === 'rejected' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            }`}>
                              {changeLine.change_order_status || 'pending'}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Integration Modal */}
          {showIntegrationModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-2xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Modal Header */}
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-3xl font-bold text-gray-900">Send Contract to Integration</h2>
                  <button 
                    onClick={() => { 
                      setShowIntegrationModal(false); 
                      setSelectedContractForIntegration(null); 
                      setSelectedIntegration(null); 
                    }} 
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {/* Contract Info */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Contract Details</h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div><span className="font-medium text-gray-600">Contract Number:</span><p className="text-gray-900">{selectedContractForIntegration?.contract_number}</p></div>
                    <div><span className="font-medium text-gray-600">Contract Name:</span><p className="text-gray-900">{selectedContractForIntegration?.contract_name}</p></div>
                    <div><span className="font-medium text-gray-600">Type:</span><p className="text-gray-900 capitalize">{selectedContractForIntegration?.contract_type || 'Not specified'}</p></div>
                    <div><span className="font-medium text-gray-600">Amount:</span><p className="text-gray-900">{formatCurrency(selectedContractForIntegration?.contract_amount || 0)}</p></div>
                  </div>
                </div>
                {/* Integration Selection */}
                <div className="mb-6">
                  <label className="block text-lg font-semibold text-gray-900 mb-3">Select Integration *</label>
                  {integrations.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      <p className="text-lg">No integrations available</p>
                      <p className="text-sm">Create a Procore integration first to send contracts to external systems.</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-60 overflow-y-auto">
                      {integrations.map((integration) => (
                        <div 
                          key={integration.vuid} 
                          onClick={() => setSelectedIntegration(integration)} 
                          className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                            selectedIntegration?.vuid === integration.vuid 
                              ? 'border-gray-800 bg-gray-50' 
                              : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900">{integration.integration_name}</h4>
                              <p className="text-sm text-gray-600 capitalize">{integration.integration_type.replace('_', ' ')}</p>
                              {integration.base_url && (
                                <p className="text-xs text-gray-500 mt-1">{integration.base_url}</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                integration.status === 'active' 
                                  ? 'bg-green-100 text-green-800' 
                                  : integration.status === 'inactive' 
                                    ? 'bg-gray-100 text-gray-800' 
                                    : 'bg-red-100 text-red-800'
                              }`}>
                                {integration.status}
                              </span>
                              {selectedIntegration?.vuid === integration.vuid && (
                                <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Action Buttons */}
                <div className="flex justify-end space-x-4">
                  <button 
                    onClick={() => { 
                      setShowIntegrationModal(false); 
                      setSelectedContractForIntegration(null); 
                      setSelectedIntegration(null); 
                    }} 
                    className="btn-claret"
                    >
                    Cancel
                  </button>
                  <button 
                    onClick={handleSendToIntegration} 
                    disabled={!selectedIntegration || sendingToIntegration} 
                    className={`font-semibold py-2 px-6 rounded-lg transition-colors ${
                      selectedIntegration && !sendingToIntegration
                        ? 'bg-gray-50 hover:bg-gray-100 text-gray-900' 
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {sendingToIntegration ? 'Sending...' : 'Send to Integration'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

      {/* Fee Settings Modal/View */}
      {showFeeSettings && selectedContract && (
        <ContractFeeSettings
          contractVuid={selectedContract.vuid}
          contractName={`${selectedContract.contract_number} - ${selectedContract.contract_name}`}
          onClose={async () => {
            setShowFeeSettings(false);
            // Refresh fee settings when modal closes to update markup values
            if (usesLineMarkups(feeLevel) && selectedContract) {
              try {
                const feeSettingsRes = await axios.get(`${baseURL}/api/contracts/${selectedContract.vuid}/fee-settings`);
                const newFeeSettings = feeSettingsRes.data || [];
                setFeeSettings(newFeeSettings);
                
                // Helper to get markup from new fee settings (before state update)
                const getMarkupFromNewSettings = (costCodeVuid, costTypeVuid) => {
                  const feeSetting = newFeeSettings.find(
                    setting => setting.cost_code_vuid === costCodeVuid && 
                               setting.cost_type_vuid === costTypeVuid
                  );
                  return feeSetting ? (feeSetting.fee_percentage || 0) : 0;
                };
                
                // Recalculate total_amount for all items based on updated markup from fee settings
                if (showItemsForm && contractItems.length > 0) {
                  const updatedItems = contractItems.map(item => {
                    if (!item.cost_code_vuid || !item.cost_type_vuid) {
                      return item;
                    }
                    
                    const oldMarkup = item.markup_percentage ?? 0;
                    const newMarkup = getMarkupFromNewSettings(item.cost_code_vuid, item.cost_type_vuid);
                    const currentTotal = parseFloat(item.total_amount || 0);
                    const total = recalculateTotalAmountWithNewMarkup(currentTotal, oldMarkup, newMarkup);
                    const quantity = parseFloat(item.quantity || 0);
                    return {
                      ...item,
                      markup_percentage: newMarkup,
                      total_amount: total,
                      unit_price: quantity ? Math.round((total / quantity) * 100) / 100 : total,
                    };
                  });
                  
                  setContractItems(updatedItems);
                  
                  // Save updated items to database
                  const itemsToSave = updatedItems.filter(item => 
                    item.cost_code_vuid && item.cost_type_vuid && item.vuid && !item.vuid.startsWith('temp-')
                  );
                  
                  if (itemsToSave.length > 0) {
                                        Promise.all(itemsToSave.map(async (item) => {
                      try {
                        await axios.put(`${baseURL}/api/project-contract-items/${item.vuid}`, {
                          total_amount: item.total_amount,
                          unit_price: item.unit_price,
                          markup_percentage: item.markup_percentage
                        });
                      } catch (err) {
                        console.warn(`Failed to update item ${item.item_number}:`, err);
                      }
                    })).then(() => {
                                          });
                  }
                }
              } catch (e) {
                console.warn('Failed to refresh fee settings:', e);
              }
            }
          }}
        />
      )}

      {showContractHistory && selectedContract && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Change History</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedContract.contract_number} - {selectedContract.contract_name}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowContractHistory(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            <div className="p-6">
              <ContractChangeHistory contractVuid={selectedContract.vuid} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectContracts;
